import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Image, Plus, Minus, ChevronDown, PauseCircle, X, Search, ShoppingCart, DollarSign, User, CreditCard, Banknote, CheckCircle, XCircle, Info, Loader2, List, Grid, Wallet, Tag, Package, AlertTriangle, Trash2, SplitSquareVertical, Bell, LucideShoppingBasket, ShoppingBasket } from 'lucide-react';
import NewCustomer from './NewCustomer';
import Input from '../components/ui/Input';
import DateTimeInput from '../components/ui/DateTimeInput';
import CheckboxInput from '../components/ui/CheckboxInput';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Modal from '../components/ui/Modal';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import {
    fetchInventoryForStore, fetchCustomerDepositBalance, fetchInventoryForStoreSales,
    fetchPOSCustomers, fetchStoresWithStaff, fetchStoretaxes, fetchDiscountRules,
    API_ROOT_PATH, currencyCodeToSymbolMap, fetchCustomerStoreLOC, fetchAllBankAccounts,
    getPendingOrders, generateUniquePoNumber,
    fetchServicesForStoreSale, fetchSurchargeValue, fetchPaymentProcessorForTerminal,
    fetchTerminalForStaff, doesStoreRequireCreditApproval,
} from '../apiHelper';
import ReceiptInvoiceModal from './ReceiptInvoiceModal';
// import { useDiscountCalculation } from '../hooks/useDiscountCalculation';
// import { useDiscountCalculation } from '../hooks/useDiscountCalculationV3';
import { useDiscountCalculation } from '../hooks/useDiscountCalculationV4';
import TrialModal from '../components/ui/TrialModal';

const client = new FastAPIClient();

const getDefaultDueDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 6);
    return date.toISOString().split('T')[0];
}

const getOrCreateIdempotencyKey = (existingKey) => {
    return existingKey ? existingKey : generateUniquePoNumber();
};

/** Money: round to whole kobo / cent */
const roundMoney = (n) => Math.round(Number(n) * 100) / 100;

/** Line total = price × qty, rounded to money */
const lineAmount = (price, qty) =>
    roundMoney(Number(price) * Number(qty));

/**
 * Snap qty to k/d when:
 * - |qty − k/d| ≤ tolerance (handles 0.33 ≈ 1/3, 0.25 = 1/4, …)
 * - unit price in kobo is divisible by d (so the split is exact money)
 * Otherwise keep 2-decimal qty. Integers and odd prices are untouched.
 */
const snapQuantityForCleanPricing = (qty, unitPrice) => {
    const q = Number(qty);
    if (!Number.isFinite(q) || q <= 0) return q;

    const priceKobo = Math.round(Number(unitPrice) * 100);
    if (!priceKobo) return Math.round(q * 100) / 100;

    const denominators = [2, 3, 4, 5, 6, 8, 10];
    const tolerance = 0.006; // 0.33 → 1/3; 0.34 does not

    let best = null;
    let bestErr = Infinity;

    for (const d of denominators) {
        if (priceKobo % d !== 0) continue; // only clean money splits
        const k = Math.round(q * d);
        if (k < 1) continue;
        const exact = k / d;
        const err = Math.abs(q - exact);
        if (err <= tolerance && err < bestErr) {
            best = exact;
            bestErr = err;
        }
    }

    if (best != null) return best;
    return Math.round(q * 100) / 100;
};

/** Display qty without ugly float noise */
const formatQty = (q) => {
    const n = Number(q);
    if (!Number.isFinite(n)) return '';
    if (Number.isInteger(n)) return String(n);
    return parseFloat(n.toFixed(6)).toString();
};

const ProductCard = ({
    product,
    cart,
    onAddToCart,
    onUpdateQuantity,
    onDirectQuantityChange,
    onFinalizeQuantity
}) => {
    const cartItem = cart.find(item => item.id === product.id);
    const attachmentUrl = product.attachments?.[0]?.attachment?.attachmenturl;
    const isService = product.isService;
    const isOutOfStock = !isService && product.stock <= 0;

    return (
        <Card className="flex flex-col h-full overflow-hidden transition-all duration-200 ease-in-out hover:shadow-lg">
            {/* Clickable area when not in cart */}
            {!cartItem ? (
                <button
                    // onClick={() => onAddToCart(product)}
                    onClick={() => {
                        const maxAvailable = product.stock;
                        const qtyToAdd = product.isService ? 1 : Math.min(1, maxAvailable);
                        if (qtyToAdd > 0 || product.isService) {
                            onAddToCart(product, qtyToAdd);
                        } else {
                            // showToast("Out of stock", 'error');
                        }
                    }}
                    disabled={isOutOfStock}
                    className="flex flex-col h-full text-left disabled:cursor-not-allowed"
                >
                    {/* Image Section */}
                    <div className="relative w-full h-32 bg-indigo-100 flex items-center justify-center rounded-t-lg overflow-hidden">
                        {attachmentUrl ? (
                            <img
                                src={`${API_ROOT_PATH}${attachmentUrl}`}
                                alt={product.name}
                                className="w-full h-full object-cover select-none pointer-events-none"
                                onContextMenu={(e) => e.preventDefault()}
                                draggable="false"
                            />
                        ) : (
                            <Image size={48} className="text-indigo-400" />
                        )}
                        {isOutOfStock && (
                            <div className="absolute inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center">
                                <span className="text-white text-lg font-bold">OUT OF STOCK</span>
                            </div>
                        )}
                    </div>

                    {/* Details Section */}
                    <div className="p-3 flex-1 flex flex-col justify-between">
                        <div>
                            <h3 className="text-md font-semibold text-text-primary mb-1 truncate" title={product.name}>
                                {product.name}
                            </h3>
                            <p className="text-xs text-text-secondary mb-2 truncate" title={product.description}>
                                {product.description}
                            </p>
                            <div className="flex items-center gap-1 text-text-primary text-sm mb-1">
                                <Wallet size={14} className="text-gmail-green" />
                                <span className="font-bold">{product.priceSymbol}{parseFloat(product.price).toFixed(2)}</span>
                            </div>
                            <div className="flex items-center gap-1 text-text-primary text-xs">
                                <Package size={14} className="text-gmail-yellow" />
                                <span>Stock: {isService ? 'Service' : product.stock}</span>
                            </div>
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                                <Tag size={12} />
                                <span>
                                    {product.tax_categories?.map(t => t.code).join(', ') || 'No Tax'}
                                </span>
                            </div>
                        </div>

                        {/* Add to Cart Text at Bottom */}
                        <div className="mt-3 text-center">
                            <span className="text-primary font-medium text-sm">
                                Tap to Add to Cart
                            </span>
                        </div>
                    </div>
                </button>
            ) : (
                <>
                    {/* Non-clickable when already in cart */}
                    <div className="relative w-full h-32 bg-indigo-50 flex items-center justify-center rounded-t-lg overflow-hidden">
                        {attachmentUrl ? (
                            <img
                                src={`${API_ROOT_PATH}${attachmentUrl}`}
                                alt={product.name}
                                className="w-full h-full object-cover select-none pointer-events-none"
                                onContextMenu={(e) => e.preventDefault()}
                                draggable="false"
                            />
                        ) : (
                            <Image size={48} className="text-indigo-400" />
                        )}
                    </div>

                    <div className="p-3 flex-1 flex flex-col justify-between">
                        <div>
                            <h3 className="text-md font-semibold text-text-primary mb-1 truncate" title={product.name}>
                                {product.name}
                            </h3>
                            <p className="text-xs text-text-secondary mb-2 truncate" title={product.description}>
                                {product.description}
                            </p>
                            <div className="flex items-center gap-1 text-text-primary text-sm mb-1">
                                <Wallet size={14} className="text-gmail-green" />
                                <span className="font-bold">{product.priceSymbol}{parseFloat(product.price).toFixed(2)}</span>
                            </div>
                            <div className="flex items-center gap-1 text-text-primary text-xs">
                                <Package size={14} className="text-gmail-yellow" />
                                <span>Stock: {isService ? 'Service' : product.stock}</span>
                            </div>
                        </div>

                        {/* Quantity Controls - prevent click propagation */}
                        <div className="mt-3">
                            <div className="flex items-center justify-center gap-2 bg-gray-50 rounded-lg p-2">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onUpdateQuantity(product.id, -1);
                                    }}
                                    className="p-1.5"
                                >
                                    <Minus size={16} />
                                </Button>
                                <Input
                                    type="number"
                                    value={cartItem.quantity}
                                    onChange={(e) => onDirectQuantityChange(product.id, e.target.value)}
                                    onBlur={() => onFinalizeQuantity(product.id)}
                                    className="w-20 text-center text-sm"
                                    min="0"
                                    step="0.01"
                                    placeholder="Qty"
                                />
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onUpdateQuantity(product.id, 1);
                                    }}
                                    className="p-1.5"
                                >
                                    <Plus size={16} />
                                </Button>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </Card>
    );
};


const ProductListItem = ({
    product,
    cart,
    onAddToCart,
    onUpdateQuantity,
    onDirectQuantityChange,
    onFinalizeQuantity,
}) => {
    const cartItem = cart.find(item => item.id === product.id);
    const attachmentUrl = product.attachments?.[0]?.attachment?.attachmenturl;
    const isService = product.isService;

    return (
        <tr className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
            <td className="px-2 py-2 w-16">
                <div className="w-12 h-12 bg-indigo-50 flex items-center justify-center rounded-md overflow-hidden">
                    {attachmentUrl ? (
                        <img
                            src={`${API_ROOT_PATH}${attachmentUrl}`}
                            alt={product.name}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <Image size={24} className="text-indigo-400" />
                    )}
                </div>
            </td>
            <td className="px-2 py-2">
                <p className="font-semibold text-text-primary">{product.name}</p>
                <p className="text-xs text-text-secondary">{product.description}</p>
            </td>
            <td className="px-2 py-2 text-center">
                <span className="font-bold text-text-primary">{product.priceSymbol}{parseFloat(product.price).toFixed(2)}</span>
            </td>
            <td className="px-2 py-2 text-center">
                <span className={`${isService ? 'text-purple-600' : (product.stock > 0 ? 'text-green-600' : 'text-red-600')} font-medium`}>
                    {isService ? 'Service' : product.stock}
                </span>
            </td>
            <td className="px-2 py-2 text-right">
                {cartItem ? (
                    <div className="flex items-center justify-end gap-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onUpdateQuantity(product.id, -1)}
                            className="p-1"
                        >
                            <Minus size={14} />
                        </Button>
                        <Input
                            type="number"
                            value={cartItem.quantity}
                            onChange={(e) => onDirectQuantityChange(product.id, e.target.value)}
                            onBlur={() => onFinalizeQuantity(product.id)}
                            className="w-14 text-center text-xs"
                            min="0"
                            step="0.01"
                        />
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onUpdateQuantity(product.id, 1)}
                            className="p-1"
                        >
                            <Plus size={14} />
                        </Button>
                    </div>
                ) : (
                    <Button
                        onClick={() => onAddToCart(product)}
                        variant="primary"
                        size="sm"
                        className="px-3 py-1.5"
                        disabled={!isService && product.stock <= 0}
                    >
                        Add
                    </Button>
                )}
            </td>
        </tr>
    );
};

const PointOfSale = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const location = useLocation();

    const [user, setUser] = useState(null);
    const [products, setProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [errorProducts, setErrorProducts] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [cart, setCart] = useState([]);
    const [showCheckoutModal, setShowCheckoutModal] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
    const [paymentAmount, setPaymentAmount] = useState('');
    const [changeDue, setChangeDue] = useState(0);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [customerSearchTerm, setSearchCustomerTerm] = useState('');
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [customerError, setCustomerError] = useState(null);
    const [stores, setStores] = useState([]);
    const [loadingStores, setLoadingStores] = useState(true);
    const [errorStores, setErrorStores] = useState(null);
    const [selectedStore, setSelectedStore] = useState(null);
    const [cashierFullName, setCashierFullName] = useState('');
    const [showReceiptInvoiceModal, setShowReceiptInvoiceModal] = useState(false);
    const [receiptData, setReceiptData] = useState(null);
    const [productOffset, setProductOffset] = useState(0);
    const [productLimit] = useState(100);
    const [hasMoreProducts, setHasMoreProducts] = useState(true);
    const [refreshProductsTrigger, setRefreshProductsTrigger] = useState(0);

    const [availableDiscountRules, setAvailableDiscountRules] = useState([]);
    // const [couponCodeInput, setCouponCodeInput] = useState('');
    const [couponCodes, setCouponCodes] = useState([]); // array of strings

    const [customerLOC, setCustomerLOC] = useState(null);
    const [loadingLOC, setLoadingLOC] = useState(false);
    const [errorLOC, setErrorLOC] = useState(null);
    const [paymentDueDate, setPaymentDueDate] = useState('');
    const [overrideTransactionDate, setOverrideTransactionDate] = useState(false);
    const [transactionDate, setTransactionDate] = useState(''); // 'YYYY-MM-DDTHH:mm' for datetime-local
    const [splitCashAmount, setSplitCashAmount] = useState('');
    const [splitCardAmount, setSplitCardAmount] = useState('');
    const [splitTransferAmount, setSplitTransferAmount] = useState('');
    const [splitCreditAmount, setSplitCreditAmount] = useState('');
    const [bankAccountOptions, setBankAccountOptions] = useState([]);
    const [loadingBankAccounts, setLoadingBankAccounts] = useState(true);
    const [errorBankAccounts, setErrorBankAccounts] = useState(null);
    const [selectedCardBankAccount, setSelectedCardBankAccount] = useState('');
    const [selectedTransferBankAccount, setSelectedTransferBankAccount] = useState('');
    const [preferredTerminalBankAccountId, setPreferredTerminalBankAccountId] = useState(null);
    const [pendingOrders, setPendingOrders] = useState([]);
    const [loadingPendingOrders, setLoadingPendingOrders] = useState(false);
    const [errorPendingOrders, setErrorPendingOrders] = useState(null);
    const [showPendingOrdersModal, setShowPendingOrdersModal] = useState(false);
    const [isPendingCheckout, setIsPendingCheckout] = useState(false);
    const [pendingIdempotencyKey, setPendingIdempotencyKey] = useState(null);
    const [showNewCustomerModal, setShowNewCustomerModal] = useState(false);
    const [showBillPreviewModal, setShowBillPreviewModal] = useState(false);
    const [billPreviewItems, setBillPreviewItems] = useState([]);
    const [billIdempotencyKey, setBillIdempotencyKey] = useState('');
    const [isFinalizingPayment, setIsFinalizingPayment] = useState(false);
    const receiptRef = useRef()

    const [showCartModal, setShowCartModal] = useState(false);

    const [showStoreSelector, setShowStoreSelector] = useState(false);

    const [bankSplits, setBankSplits] = useState([
        { id: 1, amount: '', bankAccountId: '', paymentType: 'card' }
    ]);
    const [nextBankSplitId, setNextBankSplitId] = useState(2);

    const [customerDeposit, setCustomerDeposit] = useState(0);
    const [loadingDeposit, setLoadingDeposit] = useState(false);
    const [useDeposit, setUseDeposit] = useState(false);
    const depositCache = useRef(new Map()); // customerId => {balance, timestamp}

    // how long cache is considered fresh (e.g. 5 minutes)
    const CACHE_TTL_MS = 5 * 60 * 1000;

    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

    const [showTrialModal, setShowTrialModal] = React.useState(true); // or use logic like localStorage to show once

    const [requiresCreditApproval, setRequiresCreditApproval] = useState(false);


    const availableCredit = useMemo(() => {
        if (!customerLOC) return 0;
        // console.log(customerLOC)
        return customerLOC.credit_limit - customerLOC.current_balance;
    }, [customerLOC]);


    const [isPushingToPOS, setIsPushingToPOS] = useState(false);
    const [posPaymentStatus, setPosPaymentStatus] = useState(null); // "OPEN", "COMPLETED", "FAILED"
    const [posPaidAmount, setPosPaidAmount] = useState(0);
    const [posTransactionRef, setPosTransactionRef] = useState(null);
    const [splitPosAmount, setSplitPosAmount] = useState('');
    const [terminalSurcharge, setTerminalSurcharge] = useState(0);
    const [surchargeCollected, setSurchargeCollected] = useState(0);
    const posSettlementHandledRef = useRef(false);

    // const calculateSubtotal = useMemo(() => {
    //     return cart.reduce((total, item) => total + item.price * item.quantity, 0);
    // }, [cart]);
    const calculateSubtotal = useMemo(() => {
        return roundMoney(
            cart.reduce((total, item) => total + lineAmount(item.price, item.quantity), 0)
        );
    }, [cart]);

    // const totalTaxAmount = useMemo(() => {
    //     return cart.reduce((totalTax, item) => {
    //         const taxCategories = item.tax_categories || [];
    //         const lineSubtotal = item.price * item.quantity;
    //         const lineTax = taxCategories.reduce((taxSum, tax) => {
    //             return taxSum + (lineSubtotal * (tax.rate / 100));
    //         }, 0);
    //         return totalTax + lineTax;
    //     }, 0);
    // }, [cart]);

    const totalTaxAmount = useMemo(() => {
        return roundMoney(
            cart.reduce((totalTax, item) => {
                const taxCategories = item.tax_categories || [];
                const lineSubtotal = lineAmount(item.price, item.quantity);
                const lineTax = taxCategories.reduce((taxSum, tax) => {
                    return taxSum + (lineSubtotal * (tax.rate / 100));
                }, 0);
                return totalTax + lineTax;
            }, 0)
        );
    }, [cart]);

    const {
        calculatedDiscounts,
        totalDiscountAmount,
        finalOrderTotal,
        requiresManagerApproval,
        appliedCouponCodeStatus,
        discountValidationMessage
    } = useDiscountCalculation(
        cart,
        availableDiscountRules,
        selectedCustomer,
        selectedStore,
        // couponCodeInput,
        couponCodes,
        calculateSubtotal,
        user
    );

    const grandTotal = useMemo(() => {
        return finalOrderTotal + totalTaxAmount;
    }, [finalOrderTotal, totalTaxAmount]);

    const depositCoversAll = useDeposit && customerDeposit >= grandTotal;
    // const remainingDue = useMemo(() => {
    //     const safeDeposit = Math.max(0, useDeposit ? customerDeposit : 0);
    //     return Math.max(0, grandTotal - safeDeposit);
    // }, [useDeposit, customerDeposit, grandTotal]);
    const remainingDue = useMemo(() => {
        const safeDeposit = Math.max(0, useDeposit ? customerDeposit : 0);
        const raw = Math.max(0, grandTotal - safeDeposit);
        return Math.round(raw * 100) / 100;
    }, [useDeposit, customerDeposit, grandTotal]);

    const currentNetPayable = useDeposit ? remainingDue : grandTotal;
    const remainingBalance = Math.max(0, currentNetPayable - posPaidAmount);


    useEffect(() => {
        let pollInterval;
        if (isPushingToPOS && posPaymentStatus === 'OPEN' && billIdempotencyKey) {
            pollInterval = setInterval(async () => {
                try {
                    const statusRes = await client.postData('/sales/check-pos-status', { order_id: billIdempotencyKey, store_id: selectedStore.store_id })
                    console.log(statusRes)
                    if (statusRes.status === 'COMPLETED' || statusRes.status === 'SUCCESS') {
                        // Ignore duplicate in-flight polls that also see COMPLETED
                        if (posSettlementHandledRef.current) {
                            clearInterval(pollInterval);
                            return;
                        }
                        posSettlementHandledRef.current = true;
                        clearInterval(pollInterval);

                        const settledAmount = parseFloat(statusRes.transactionValue || 0);
                        const surcharge = Math.max(0, terminalSurcharge || 0);
                        const towardSale = Math.max(0, Math.round((settledAmount - surcharge) * 100) / 100);

                        setPosPaidAmount(prev => prev + towardSale);
                        setSurchargeCollected(prev => prev + surcharge);
                        setPosTransactionRef(statusRes.transactionRef);

                        setPosPaymentStatus('COMPLETED');
                        setIsPushingToPOS(false);
                        showToast("Terminal payment confirmed!", "success");
                        clearInterval(pollInterval);

                        const targetAmount = useDeposit ? remainingDue : grandTotal;
                        // const unfulfilled = Math.max(0, targetAmount - settledAmount);
                        const unfulfilled = Math.max(0, targetAmount - (posPaidAmount + towardSale));

                        if (unfulfilled <= 0) {
                            showToast("Full terminal payment confirmed!", "success");
                            // setSelectedPaymentMethod('card');
                            // setPaymentAmount(targetAmount.toFixed(2));
                        } else {
                            showToast(
                                `Partial terminal payment received: ${currencyCodeToSymbolMap['NGN']}${settledAmount.toFixed(2)}. Remaining balance: ${currencyCodeToSymbolMap['NGN']}${unfulfilled.toFixed(2)}`,
                                "info"
                            );

                            // ONLY force-switch to split when we were in pure-POS mode
                            if (selectedPaymentMethod !== 'split') {
                                // enable split mode and pre-fill remaining balance into split inputs for the cashier resolution
                                setSelectedPaymentMethod('split');
                                setSplitCashAmount(unfulfilled.toFixed(2));
                            }
                        }
                    } else if (statusRes.status === 'FAILED') {
                        setPosPaymentStatus('FAILED');
                        setIsPushingToPOS(false);

                        // Confirmed terminal failure -> burn this order_id at the gateway.
                        // Next push must use a brand-new idempotency key
                        setBillIdempotencyKey('');

                        const reason = statusRes.response || statusRes.message || 'declined';
                        showToast(
                            `Terminal transaction failed (${reason}). You can retry with a new attempt.`,
                            "error"
                        );
                        clearInterval(pollInterval);
                    }
                } catch (err) {
                    console.error(err)
                    showToast(err.response?.data?.detail || "Status check error", "error");
                }
            }, 3000)
        }
        return () => clearInterval(pollInterval);
    }, [isPushingToPOS, posPaymentStatus, billIdempotencyKey, selectedStore, useDeposit, remainingDue, grandTotal, showToast]);

    const handlePushToPOS = async (amountOverride = null) => {
        if (selectedPaymentMethod !== 'split') {
            setSelectedPaymentMethod('pos');
        }
        // setSelectedPaymentMethod('pos');
        setIsPushingToPOS(true);
        setPosPaymentStatus('OPEN');
        posSettlementHandledRef.current = false;

        const orderIdempotencyKey = getOrCreateIdempotencyKey(billIdempotencyKey);
        setBillIdempotencyKey(orderIdempotencyKey);

        // Push the net remaining amount required to complete the sale.
        // const targetAmount = posPaidAmount > 0 ? remainingBalance : (useDeposit ? remainingDue : grandTotal);
        let targetAmount;
        if (amountOverride != null && amountOverride > 0) {
            targetAmount = amountOverride;
        } else if (posPaidAmount > 0) {
            targetAmount = remainingBalance;
        } else {
            targetAmount = useDeposit ? remainingDue : grandTotal;
        }

        const surcharge = Math.max(0, terminalSurcharge || 0)
        targetAmount = Math.round((targetAmount + surcharge) * 100) / 100;

        try {
            // ...
            const pushRes = await client.postData('/sales/push-pos-payment', {
                order_id: orderIdempotencyKey,
                amount: targetAmount,
                store_id: selectedStore.store_id,
            });
            if (pushRes.status !== 'OPEN') {
                throw new Error(pushRes.message || "Could not push to terminal.");
            }
            showToast("Transaction pushed to terminal. Please request customer to pay on device.", "info");
        } catch (err) {
            setIsPushingToPOS(false);
            setPosPaymentStatus('FAILED');

            // Push never opened successfully (or gateway rejected it).
            // Discard the key we just allocated so the next attempt is clean.
            setBillIdempotencyKey('');

            const errMsg = err.response?.data?.detail || err.message || "Failed to push to terminal.";
            showToast(errMsg, "error");
        }
    }

    const fetchDepositIfNeeded = useCallback(async (customerId) => {
        if (!customerId) {
            setCustomerDeposit(0);
            setUseDeposit(false);
            return;
        }

        const cached = depositCache.current.get(customerId);
        const now = Date.now();

        // use cache if fresh
        if (cached && now - cached.timestamp < CACHE_TTL_MS) {
            if (customerDeposit !== cached.balance) {
                setCustomerDeposit(cached.balance);
            }
            // fetchDepositIfNeeded(customerId);
            return;
        }

        // otherwise fetch (but don't block UI)
        setLoadingDeposit(true);
        try {
            const balance = await fetchCustomerDepositBalance(customerId);
            const fresh = balance ?? 0;
            setCustomerDeposit(fresh);
            depositCache.current.set(customerId, { balance: fresh, timestamp: now });
        } catch (err) {
            console.error("Deposit fetch failed", err);
            setCustomerDeposit(0);
        } finally {
            setLoadingDeposit(false);
        }
    }, []);

    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to use the Point of Sale.", "info");
            navigate('/login');
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast, navigate]);

    useEffect(() => {
        const loadStores = async () => {
            if (!user) return;
            setLoadingStores(true);
            setErrorStores(null);
            try {
                const fetchedStoresWithStaff = await fetchStoresWithStaff();
                let foundCashierName = '';
                for (const store of fetchedStoresWithStaff) {
                    const cashierStaffEntry = store.staff?.find(staffMember => staffMember.member_id === user.users_id);
                    if (cashierStaffEntry) {
                        foundCashierName = `${cashierStaffEntry.user.first_name || ''} ${cashierStaffEntry.user.last_name || ''}`.trim();
                        break;
                    }
                }
                setCashierFullName(foundCashierName);
                const filteredStores = fetchedStoresWithStaff.filter(store =>
                    store.storetype !== 'WHS' &&
                    store.staff && store.staff.some(staffMember => staffMember.member_id === user.users_id)
                );
                // console.log(filteredStores)
                setStores(filteredStores.map(store => ({
                    label: store.identifier,
                    value: store.store_id,
                    storeData: store
                })));
                const storeIdFromUrl = location.state?.storeId;
                if (storeIdFromUrl) {
                    const preselectedStore = filteredStores.find(s => s.store_id === storeIdFromUrl);
                    if (preselectedStore) {
                        setSelectedStore(preselectedStore);
                        setTimeout(() => showToast(`Store "${preselectedStore.identifier}" pre-selected from URL.`, 'info'), 0);
                    } else {
                        setTimeout(() => showToast(`Pre-selected store ID "${storeIdFromUrl}" not found or you are not assigned to it. Please select a store.`, 'error'), 0);
                    }
                } else if (filteredStores.length === 1) {
                    setSelectedStore(filteredStores[0]);
                    setTimeout(() => showToast(`Auto-selected the only available store: "${filteredStores[0].identifier}".`, 'info'), 0);
                }
            } catch (err) {
                const errorMessage = err.response?.data?.detail || `Failed to load stores: ${err.message || err}`;
                setErrorStores(errorMessage);
                setTimeout(() => showToast(errorMessage, 'error'), 0);
            } finally {
                setLoadingStores(false);
            }
        };
        loadStores();
    }, [user, showToast, location.state]);


    useEffect(() => {
        const loadProducts = async () => {
            if (!user || !selectedStore) {
                setProducts([]);
                setLoadingProducts(false);
                setHasMoreProducts(false);
                return;
            }
            setLoadingProducts(true);
            setErrorProducts(null);
            try {
                const payload = {
                    users_id: user.users_id,
                    logon_id: user.logon_id,
                    hpwc: user.hpwc,
                    store_id: selectedStore.store_id,
                    offset: productOffset,
                    limit: productLimit
                };
                // const fetchedInventoryLots = await fetchInventoryForStore(payload);
                const fetchedInventoryLots = await fetchInventoryForStoreSales(payload);
                const fetchedServicesItems = await fetchServicesForStoreSale(payload); // ADDED THIS LINE

                const allLots = [...fetchedInventoryLots, ...fetchedServicesItems];
                const aggregatedProductsMap = new Map();

                // fetchedInventoryLots.forEach(lot => {
                allLots.forEach(lot => {
                    const catentryId = lot.catentry.catentry_id;
                    if (!aggregatedProductsMap.has(catentryId)) {
                        const isServiceFromCatalog = lot.catentry.catgpenrels?.[0]?.catalog?.identifier?.toLowerCase().includes("service");
                        const isServiceFromLot = lot.lot_id === 0 && lot.current_quantity == 0;
                        const isService = isServiceFromCatalog || isServiceFromLot;
                        aggregatedProductsMap.set(catentryId, {
                            id: catentryId,
                            name: lot.catentry.productname,
                            description: lot.catentry.partnumber || lot.catentry.productname,
                            price: parseFloat(lot.catentry.listprice?.listprice || 0),
                            priceSymbol: lot.catentry.listprice?.currency_rel?.setcnote || "$",
                            currencyCode: lot.catentry.listprice?.currency_rel?.setccurr || "USD",
                            stock: 0,
                            category: lot.catentry.catgpenrels?.[0]?.category?.identifier || "Uncategorized",
                            category_id: lot.catentry.catgpenrels?.[0]?.category?.category_id || null,
                            catalog: lot.catentry.catgpenrels?.[0]?.catalog?.identifier || "Unattached",
                            isService: isService,
                            attachments: lot.catentry.attachments,
                            tax_categories: lot.catentry.tax_categories || []
                        });
                    }
                    const currentProduct = aggregatedProductsMap.get(catentryId);
                    // services stay at 0 stock but 'isService' allows them to be added to cart
                    currentProduct.stock += lot.current_quantity;
                });
                const filteredAndFormattedProducts = Array.from(aggregatedProductsMap.values());
                setProducts(prevProducts =>
                    productOffset === 0 ? filteredAndFormattedProducts : [...prevProducts, ...filteredAndFormattedProducts]
                );
                setHasMoreProducts(fetchedInventoryLots.length === productLimit);
            } catch (err) {
                const errorMessage = err.response?.data?.detail || `Failed to load products: ${err.message || err}`;
                setErrorProducts(errorMessage);
                setTimeout(() => showToast(errorMessage, 'error'), 0);
            } finally {
                setLoadingProducts(false);
            }
        };
        loadProducts();
    }, [user, selectedStore, productOffset, productLimit, showToast, refreshProductsTrigger]);

    const fetchCustomers = useCallback(async (searchQuery = '') => {
        if (!user || !selectedStore) return;
        setLoadingCustomers(true);
        setCustomerError(null);
        try {
            const fetchedCustomers = await fetchPOSCustomers({ ...user, store_id: selectedStore.store_id }, searchQuery);
            // console.log(fetchedCustomers)
            setCustomerOptions(fetchedCustomers.map(customer => ({
                label: `${customer.user?.first_name || ''} ${customer.user?.middle_name || ''} ${customer.user?.last_name || ''}`,
                // label: `${customer.user?.first_name || ''} ${customer.user?.last_name || ''} (${customer.user?.credential?.logon_id || 'N/A'})`,
                value: customer.member_id,
                customerData: customer
            })));
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to load customers: ${err.message || err}`;
            setCustomerError(errorMessage);
            setTimeout(() => showToast(errorMessage, 'error'), 0);
        } finally {
            setLoadingCustomers(false);
        }
    }, [user, selectedStore, showToast]);

    useEffect(() => {
        if (user) {
            fetchCustomers();
        }
    }, [user, fetchCustomers]);

    useEffect(() => {
        if (customerOptions.length == 0 || selectedCustomer) return;

        const walkInKeywords = ['walk in', 'walk-in', 'walkin', 'cash customer', 'counter sale'];

        const walkInCustomerOption = customerOptions.find(option => {
            const fullName = `${option.customerData.user.first_name || ''} ${option.customerData.user.last_name || ''}`.trim().toLowerCase();
            return walkInKeywords.some(keyword => fullName.includes(keyword));
        });

        // const walkInCustomerOption = customerOptions.find(option => {
        //     const fullName = `${option.customerData.user.first_name || ''} ${option.customerData.user.last_name || ''}`.trim();
        //     return fullName.toLowerCase().includes('walk in');
        // })

        if (walkInCustomerOption) {
            setSelectedCustomer(walkInCustomerOption.customerData);
            // showToast(`Auto-selected walk-in customer: ${walkInCustomerOption.label}`, 'info')
        }
    }, [customerOptions, selectedCustomer, showToast])

    useEffect(() => {
        const handler = setTimeout(() => {
            if (user) {
                fetchCustomers(customerSearchTerm);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [customerSearchTerm, user, fetchCustomers]);

    useEffect(() => {
        if (selectedCustomer?.member_id) {
            fetchDepositIfNeeded(selectedCustomer.member_id);
        }
    }, [selectedCustomer?.member_id, fetchDepositIfNeeded]);

    useEffect(() => {
        const loadBankAccounts = async () => {
            setLoadingBankAccounts(true);
            setErrorBankAccounts(null);
            try {
                const fetchedAccounts = await fetchAllBankAccounts();
                // console.log(fetchedAccounts)
                const formattedOptions = fetchedAccounts.map(account => ({
                    label: `${account.name} (${account.account_id})`,
                    value: String(account.account_id),
                }));
                setBankAccountOptions(formattedOptions);
            } catch (err) {
                const errorMessage = err.response?.data?.detail || `Failed to load bank accounts: ${err.message || err}`;
                setErrorBankAccounts(errorMessage);
                showToast(errorMessage, 'error');
            } finally {
                setLoadingBankAccounts(false);
            }
        };
        loadBankAccounts();
    }, [showToast]);

    useEffect(() => {
        if (!selectedStore?.store_id) {
            setTerminalSurcharge(0);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const attr = await fetchSurchargeValue(selectedStore.store_id);
                // numericvalue is the surcharge; fall back to 0 if missing / null
                const value = attr?.numericvalue != null ? parseFloat(attr.numericvalue) : 0;
                if (!cancelled) setTerminalSurcharge(isNaN(value) ? 0 : value);
            } catch (err) {
                console.error('Failed to load terminal surcharge', err);
                if (!cancelled) setTerminalSurcharge(0);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedStore?.store_id]);

    useEffect(() => {
        let cancelled = false;
        async function loadApprovalFlag() {
            if (!selectedStore?.store_id) {
                setRequiresCreditApproval(false);
                return;
            }
            try {
                const res = await doesStoreRequireCreditApproval(selectedStore.store_id);
                // console.log(res)
                if (!cancelled) setRequiresCreditApproval(!!res?.status);
            } catch {
                if (!cancelled) setRequiresCreditApproval(false);
            }
        }
        loadApprovalFlag();
        return () => { cancelled = true; };
    }, [selectedStore?.store_id]);

    useEffect(() => {
        if (!selectedStore?.store_id) {
            setPreferredTerminalBankAccountId(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const terminal = await fetchTerminalForStaff();
                // console.log(terminal)
                if (cancelled || !terminal) {
                    if (!cancelled) setPreferredTerminalBankAccountId(null);
                    return;
                }
                // Prefer the first terminal assigned to this store
                const terminalId = terminal.terminal_id;
                // console.log(terminalId)
                const processor = await fetchPaymentProcessorForTerminal(terminalId);
                // console.log('PROCESSOR', processor)

                if (cancelled) return;
                const bankId = processor?.bankaccount_id ?? null;
                setPreferredTerminalBankAccountId(bankId != null ? String(bankId) : null);
            } catch (err) {
                console.error('Failed to resolve terminal payment processor', err);
                if (!cancelled) setPreferredTerminalBankAccountId(null);
            }
        })();
        return () => { cancelled = true; };
    }, [selectedStore?.store_id]);

    useEffect(() => {
        const loadDiscountRules = async () => {
            if (!user) return;
            try {
                const payload = {
                    users_id: user.users_id,
                    logon_id: user.logon_id,
                    hpwc: user.hpwc,
                    store_id: selectedStore?.store_id,
                    offset: 0,
                    limit: 1000
                };
                // console.log(payload)
                const rules = await fetchDiscountRules(payload);
                console.log(rules)
                setAvailableDiscountRules(rules);
            } catch (err) {
                console.error("Failed to fetch discount rules:", err);
                showToast("Failed to load discount rules.", 'error');
            }
        };
        loadDiscountRules();
    }, [user, selectedStore, showToast]);


    const loadCustomerLOC = useCallback(async () => {
        if (!user || !selectedCustomer || !selectedStore) {
            setCustomerLOC(null);
            return;
        }
        setLoadingLOC(true);
        setErrorLOC(null);
        try {
            const payload = {
                customer_id: selectedCustomer.member_id,
                store_id: selectedStore.store_id,
            };
            const locData = await fetchCustomerStoreLOC(payload);
            setCustomerLOC(locData);
        } catch (err) {
            console.error("Error fetching customer LOC:", err);
            const errorMessage = err.response?.data?.detail || `Failed to load customer credit line: ${err.message || err}`;
            setErrorLOC(errorMessage);
            showToast(errorMessage, "error");
            setCustomerLOC(null);
        } finally {
            setLoadingLOC(false);
        }
    }, [user, selectedCustomer, selectedStore, showToast]);

    useEffect(() => {
        loadCustomerLOC();
    }, [loadCustomerLOC]);

    useEffect(() => {
        if (selectedPaymentMethod === 'credit') {
            setPaymentDueDate(getDefaultDueDate());
        } else {
            setPaymentDueDate('')
        }
    }, [selectedPaymentMethod])

    const fetchPendingOrders = useCallback(async () => {
        if (!user || !selectedStore) {
            setPendingOrders([]);
            setLoadingPendingOrders(false);
            return;
        }
        setLoadingPendingOrders(true);
        setErrorPendingOrders(null);
        try {
            const payload = { store_id: selectedStore.store_id };
            const response = await getPendingOrders(payload);
            const processedOrders = response.map(order => ({
                ...order,
                items: order.items.map(item => ({
                    catentry_id: item.catentry_id,
                    name: item.productname,
                    quantity: item.quantity || 1
                })),
                customer_id: order.customer_id, // Added
                member_id: order.member_id,     // Added
                store_id: order.store_id,       // Added
                currency: order.currency        // Added
            }));
            setPendingOrders(processedOrders || []);
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to load pending orders: ${err.message || err}`;
            setErrorPendingOrders(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setLoadingPendingOrders(false);
        }
    }, [user, selectedStore, showToast]);

    useEffect(() => {
        if (selectedStore) {
            fetchPendingOrders();
        }
    }, [selectedStore, fetchPendingOrders]);

    const filteredProducts = useMemo(() => {
        if (!searchTerm.trim()) {
            return products;
        }
        const lowerCaseSearchTerm = searchTerm.toLowerCase();
        return products.filter(product =>
            product.name.toLowerCase().includes(lowerCaseSearchTerm) ||
            product.description.toLowerCase().includes(lowerCaseSearchTerm) ||
            product.category.toLowerCase().includes(lowerCaseSearchTerm) ||
            product.catalog.toLowerCase().includes(lowerCaseSearchTerm)
        );
    }, [products, searchTerm]);

    const addToCart = useCallback((product, quantity = 1) => {
        // console.log(`PRODUCT: ${product} QUANTITY: ${quantity}`)
        setCart(prevCart => {
            const existingItem = prevCart.find(item => item.id === product.id);
            if (existingItem) {
                const newQuantity = existingItem.quantity + quantity;
                if (product.isService || newQuantity <= product.stock) {
                    setTimeout(() => showToast(`Added ${quantity} more "${product.name}" to cart.`, 'info'), 0);
                    return prevCart.map(item =>
                        item.id === product.id ? { ...item, quantity: newQuantity } : item
                    );
                } else {
                    setTimeout(() => showToast(`Cannot add more "${product.name}". Max stock reached.`, 'error'), 0);
                    return prevCart;
                }
            } else {
                if (product.isService || product.stock >= quantity) {
                    setTimeout(() => showToast(`Added "${product.name}" to cart.`, 'success'), 0);
                    return [...prevCart, { ...product, quantity, currencyCode: product.currencyCode, priceSymbol: product.priceSymbol, category_id: product.category_id }];
                } else {
                    setTimeout(() => showToast(`"${product.name}" is out of stock.`, 'error'), 0);
                    return prevCart;
                }
            }
        });
    }, [showToast]);

    const handleCheckoutPendingOrder = useCallback(async (order) => {
        const itemsToAdd = order.items;
        const newCartItems = [];
        itemsToAdd.forEach(item => {
            const product = products.find(p => p.id === item.catentry_id);
            if (product) {
                const quantity = item.quantity || 1;
                if (product.isService || product.stock >= quantity) {
                    newCartItems.push({ ...product, quantity, currencyCode: product.currencyCode, priceSymbol: product.priceSymbol, category_id: product.category_id });
                    showToast(`Added "${product.name}" to cart for checkout.`, 'success');
                } else {
                    showToast(`Cannot add "${product.name}" to cart. Out of stock.`, 'error');
                }
            } else {
                showToast(`Product "${item.name}" not found in current inventory.`, 'error');
            }
        });
        if (newCartItems.length > 0) {
            setCart(prevCart => [...prevCart, ...newCartItems]);
            // Set the customer based on customer_id from the order
            const customer = customerOptions.find(opt => opt.value === order.customer_id)?.customerData || null;
            setSelectedCustomer(customer);
            // Set the store based on store_id from the order
            const store = stores.find(s => s.value === order.store_id)?.storeData || null;
            if (store && store.store_id !== selectedStore?.store_id) {
                setSelectedStore(store);
                setProducts([]);
                setProductOffset(0);
            }
            // Ensure currency consistency
            if (order.currency !== selectedStore?.setccurr && cart.length > 0) {
                showToast(`Warning: Order currency (${order.currency}) may differ from store currency.`, 'warning');
            }
            setShowCheckoutModal(true);
            setShowPendingOrdersModal(false);
            setIsPendingCheckout(true);
            setPendingIdempotencyKey(order.idempotency_key);
        }
    }, [products, showToast, customerOptions, stores, selectedStore, cart]);

    const handleCancelOrder = useCallback(async (orders_id, idempotencyKey) => {
        if (!window.confirm('Are you sure you want to cancel this order?')) return;
        setLoadingPendingOrders(true);
        try {
            const payload = { store_id: selectedStore.store_id, orders_id: orders_id, idempotency_key: idempotencyKey }
            // console.log(payload)
            await client.postData('/sales/cancel-order', payload);
            showToast('Order canceled successfully.', 'success');
            await fetchPendingOrders();
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to cancel order: ${err.message || err}`;
            showToast(errorMessage, 'error');
        } finally {
            setLoadingPendingOrders(false);
        }
    }, [showToast, fetchPendingOrders]);

    const updateCartQuantity = useCallback((productId, delta) => {
        setCart(prevCart => {
            return prevCart.map(item => {
                if (item.id === productId) {
                    const newQuantity = item.quantity + delta;
                    const product = products.find(p => p.id === productId);
                    if (!product) return item;
                    if (newQuantity <= 0) {
                        setTimeout(() => showToast(`Removed "${item.name}" from cart.`, 'info'), 0);
                        return null;
                    }
                    if (product.isService || newQuantity <= product.stock) {
                        return { ...item, quantity: newQuantity };
                    } else {
                        setTimeout(() => showToast(`Cannot add more "${item.name}". Max stock reached.`, 'error'), 0);
                        return item;
                    }
                }
                return item;
            }).filter(Boolean);
        });
    }, [products, showToast]);


    // const handleQuantityChange = useCallback((productId, value) => {
    //     setCart(prevCart => {
    //         let newQuantity = parseFloat(value);

    //         // Handle invalid input
    //         if (isNaN(newQuantity) || newQuantity < 0.01) {
    //             newQuantity = 0.01;
    //         }

    //         // Round to 2 decimal places to avoid floating point weirdness
    //         newQuantity = Math.round(newQuantity * 100) / 100;

    //         const product = products.find(p => p.id === productId);
    //         if (!product) return prevCart;

    //         // For non-services: don't exceed stock (allow decimals up to stock)
    //         if (!product.isService && newQuantity > product.stock) {
    //             showToast(`Max available: ${product.stock}. Adjusted to max stock.`, 'error');
    //             newQuantity = product.stock;
    //         }

    //         return prevCart.map(item =>
    //             item.id === productId ? { ...item, quantity: newQuantity } : item
    //         );
    //     });
    // }, [products, showToast]);

    const handleQuantityChange = useCallback((productId, value) => {
        setCart(prevCart => {
            // 1. Allow empty string so user can clear the input
            // 2. Allow trailing decimals like "1." so they can finish typing "1.5"
            if (value === '' || value.endsWith('.') || isNaN(parseFloat(value))) {
                return prevCart.map(item =>
                    item.id === productId ? { ...item, quantity: value } : item
                );
            }

            let newQuantity = parseFloat(value);

            // If the user types something that isn't a number yet, keep the string
            if (isNaN(newQuantity)) {
                return prevCart.map(item =>
                    item.id === productId ? { ...item, quantity: value } : item
                );
            }

            const product = products.find(p => p.id === productId);
            if (!product) return prevCart;

            // Stock check (Keep this, but maybe remove the Toast until onBlur 
            // to avoid spamming the user while they type)
            if (!product.isService && newQuantity > product.stock) {
                newQuantity = product.stock;
            }

            return prevCart.map(item =>
                item.id === productId ? { ...item, quantity: newQuantity } : item
            );
        });
    }, [products]);

    // const finalizeQuantity = useCallback((productId) => {
    //     setCart(prevCart => {
    //         return prevCart.map(item => {
    //             if (item.id !== productId) return item;

    //             let finalQty = parseFloat(item.quantity);

    //             // Now we enforce the "hard" rules
    //             if (finalQty === '' || isNaN(parseFloat(finalQty)) || finalQty < 0.01) {
    //                 finalQty = 0.01;
    //             } else {
    //                 finalQty = parseFloat(finalQty)
    //             }

    //             const product = products.find(p => p.id === productId);
    //             if (product && !product.isService && finalQty > product.stock) {
    //                 finalQty = product.stock
    //                 showToast(`Adjusted to max stock: ${product.stock}`, 'info')
    //             }

    //             // Apply rounding once at the end
    //             finalQty = Math.round(finalQty * 100) / 100;

    //             return { ...item, quantity: finalQty };
    //         });
    //     });
    // }, [products, showToast]);

    const finalizeQuantity = useCallback((productId) => {
        setCart(prevCart => {
            return prevCart.map(item => {
                if (item.id !== productId) return item;

                let finalQty = parseFloat(item.quantity);

                if (item.quantity === '' || isNaN(finalQty) || finalQty < 0.01) {
                    finalQty = 0.01;
                }

                const product = products.find(p => p.id === productId);
                if (product && !product.isService && finalQty > product.stock) {
                    finalQty = product.stock;
                    showToast(`Adjusted to max stock: ${product.stock}`, 'info');
                }

                // Snap near-simple fractions when unit price allows clean money splits
                finalQty = snapQuantityForCleanPricing(finalQty, item.price);

                return { ...item, quantity: finalQty };
            });
        });
    }, [products, showToast]);

    const removeFromCart = useCallback((productId) => {
        setCart(prevCart => {
            const removedItem = prevCart.find(item => item.id === productId);
            if (removedItem) {
                setTimeout(() => showToast(`Removed "${removedItem.name}" from cart.`, 'info'), 0);
            }
            return prevCart.filter(item => item.id !== productId);
        });
    }, [showToast]);

    const handleClearCart = useCallback(() => {
        if (window.confirm('Are you sure you want to clear the entire cart?')) {
            setCart([]);
            // setCouponCodeInput('');
            setCouponCodes([]);
            showToast('Cart cleared.', 'info');
        }
    }, [showToast]);


    // useEffect(() => {
    //     const paid = parseFloat(paymentAmount) || 0;
    //     const effectiveDue = useDeposit ? remainingDue : grandTotal;
    //     setChangeDue(paid - effectiveDue);
    // }, [paymentAmount, useDeposit, remainingDue, grandTotal]);

    useEffect(() => {
        const paid = parseFloat(paymentAmount) || 0;
        // Always measure against what the cashier still owes (deposit-aware)
        const effectiveDue = remainingDue;
        const raw = paid - effectiveDue;
        // Snap tiny float noise to zero
        setChangeDue(Math.abs(raw) < 0.005 ? 0 : raw);
    }, [paymentAmount, remainingDue]);

    // REVIEW 
    // useEffect(() => {
    //     if (showCheckoutModal && paymentAmount === '') {
    //         setPaymentAmount(grandTotal.toFixed(2));
    //         setChangeDue(0);
    //     }
    // }, [showCheckoutModal, grandTotal])

    // Ensure payment amount reflects discounted total when checkout opens
    // useEffect(() => {
    //     if (showCheckoutModal) {
    //         const amountToPay = useDeposit
    //             ? Math.max(0, grandTotal - customerDeposit)
    //             : grandTotal;

    //         setPaymentAmount(amountToPay.toFixed(2));
    //         setChangeDue(0);
    //     }
    // }, [showCheckoutModal, grandTotal, useDeposit, customerDeposit]);
    useEffect(() => {
        if (showCheckoutModal) {
            const amountToPay = useDeposit
                ? Math.max(0, grandTotal - customerDeposit)
                : grandTotal;
            const rounded = Math.round(amountToPay * 100) / 100;
            setPaymentAmount(rounded.toFixed(2));
            setChangeDue(0);
        }
    }, [showCheckoutModal, grandTotal, useDeposit, customerDeposit]);

    const handleCheckout = useCallback(() => {
        if (cart.length === 0) {
            showToast("Cart is empty. Please add items before checking out.", 'info');
            return;
        }
        if (!selectedStore) {
            showToast("Please select a store to proceed with checkout.", 'error');
            return;
        }
        if (!selectedCustomer) {
            showToast("Please select a customer before proceeding to checkout.", 'error');
            return;
        }

        setShowCheckoutModal(true);
        // setPaymentAmount(grandTotal.toFixed(2));
        // setChangeDue(0);
        setSelectedPaymentMethod('');
        setPaymentDueDate('');
        setSplitCashAmount('');
        setSplitCardAmount('');
        setSplitTransferAmount('');
        setSplitCreditAmount('');
        setSelectedCardBankAccount('');
        setSelectedTransferBankAccount('');
        // setCouponCodes([]);
        setSplitPosAmount('');
        setSurchargeCollected(0);

        setIsPushingToPOS(false);
        setPosPaymentStatus(null);
        setPosPaidAmount(0);
        setPosTransactionRef(null);


        setBankSplits([
            { id: 1, amount: '', bankAccountId: '', paymentType: 'card' }
        ]);
        setNextBankSplitId(2);

        // reset payment amount so it picks up the discounted total
        setPaymentAmount('');
        setChangeDue(0);



        if (selectedPaymentMethod === 'split' && useDeposit && remainingDue > 0) {
            setSplitCashAmount(remainingDue.toFixed(2));
        }
        // reset deposit usage when opening checkout
        setUseDeposit(false);
    }, [cart, grandTotal, showToast, selectedStore, selectedCustomer]);

    const handleGenerateBill = useCallback(async () => {
        if (cart.length === 0) {
            showToast("Cart is empty. Please add items before generating a bill.", 'info');
            return;
        }
        if (!selectedStore) {
            showToast("Please select a store to generate a bill.", 'error');
            return;
        }
        if (!selectedCustomer) {
            showToast("Please select a customer before generating a bill.", 'error');
            return;
        }
        if (requiresManagerApproval) {
            showToast("Manager approval is required to generate this bill due to applied discounts.", "error");
            return;
        }
        setLoadingProducts(true);
        try {
            const currencyCode = selectedStore?.setccurr || (cart.length > 0 ? cart[0].currencyCode : "NGN");
            // const idempotencyKey = crypto.randomUUID().replace(/-/g, '').toUpperCase().substring(0, 15);
            // const idempotencyKey = generateUniquePoNumber();
            const idempotencyKey = getOrCreateIdempotencyKey(billIdempotencyKey);
            const pendingOrderPayload = {
                member_id: user.users_id,              // Added
                customer_id: selectedCustomer.member_id, // Added
                store_id: selectedStore.store_id,      // Added
                currency: currencyCode,                 // Added
                subtotal: calculateSubtotal,
                totaltax: totalTaxAmount,
                totaldiscount: totalDiscountAmount,
                total_amount: grandTotal,
                items: cart.map(item => ({
                    catentry_id: item.id,
                    quantity: item.quantity,
                    price: item.price,
                    productname: item.name,
                    // Ensure this doesn't break if tax_categories is missing/empty
                    // tax_breakdown: (item.tax_categories || []).map(tax => ({ ... }))
                })),
                applied_discounts: calculatedDiscounts,

                // Optional: list of codes if the backend needs it
                // coupon_codes: couponCodes.filter(Boolean),

                idempotency_key: idempotencyKey
            };
            const response = await client.postData('/sales/create-pending', pendingOrderPayload);
            showToast(response.message || "Bill generated successfully!", 'success');

            // CLEANUP – same as finalizeSale
            setCart([]);                     // ← clear cart
            // setCouponCodeInput('');          // ← clear applied discount
            setCouponCodes([]);              // ← clear applied discounts
            setProductOffset(0);             // ← reset product pagination
            setRefreshProductsTrigger(prev => prev + 1); // ← refresh inventory
            // optionally also reset customer if you want a fresh start
            setSelectedCustomer(null);

            // fetch latest pending orders to verify integrity
            await new Promise((resolve) => {
                setPendingOrders(prev => {
                    // Re-fetch or trust API response? we'll re-fetch safely
                    fetchPendingOrders().then(() => resolve());
                    return prev;
                });
            });

            // now use updated state via ref or wair for re-render - but better: re-fetch and use latest
            const updatedOrders = await getPendingOrders({ store_id: selectedStore.store_id });
            const latestPending = updatedOrders.find(o => o.idempotency_key == idempotencyKey);

            // find the just-created pending order
            // const latestPending = pendingOrders.find(o => o.idempotency_key == idempotencyKey);
            if (latestPending) {
                setBillIdempotencyKey(idempotencyKey)
                setBillPreviewItems(latestPending.items.map(item => ({
                    name: item.productname || item.name,
                    quantity: item.quantity
                })));
                setShowBillPreviewModal(true)
            } else {
                showToast("Bill created but failed to verify items. Please try again.", 'error');
            }

        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to Generate Slip: ${err.message || err}`;
            showToast(errorMessage, 'error');
        } finally {
            setLoadingProducts(false);
        }
    }, [user, selectedCustomer, selectedStore, cart, showToast, calculateSubtotal, totalTaxAmount, totalDiscountAmount, grandTotal, calculatedDiscounts, requiresManagerApproval, fetchPendingOrders, pendingOrders]);

    // const handlePaymentAmountChange = useCallback((e) => {
    //     const amount = parseFloat(e.target.value);
    //     setPaymentAmount(e.target.value);
    //     if (!isNaN(amount)) {
    //         setChangeDue(amount - grandTotal);
    //     } else {
    //         setChangeDue(0);
    //     }
    // }, [grandTotal]);
    const handlePaymentAmountChange = useCallback((e) => {
        const amount = parseFloat(e.target.value);
        setPaymentAmount(e.target.value);
        if (!isNaN(amount)) {
            const raw = amount - remainingDue;
            setChangeDue(Math.abs(raw) < 0.005 ? 0 : raw);
        } else {
            setChangeDue(0);
        }
    }, [remainingDue]);


    // const closeCheckoutModal = () => {
    //     setShowCheckoutModal(false);

    //     // optionally reset split state here too for consistency
    //     setBankSplits([{ id: 1, amount: '', bankAccountId: '', paymentType: 'card' }]);
    //     setNextBankSplitId(2);

    //     // ... other resets if desired
    // }

    const closeCheckoutModal = () => {
        setShowCheckoutModal(false);

        // Reset ONLY payment-related states.
        // DO NOT clear couponCodes — user may want to go back and still have discounts
        setBankSplits([{ id: 1, amount: '', bankAccountId: '', paymentType: 'card' }]);
        setNextBankSplitId(2);
        setSelectedPaymentMethod('');
        setPaymentAmount('');
        setChangeDue(0);
        setUseDeposit(false);
        // setCouponCodes([]);   ←←← DO NOT UNCOMMENT THIS
        setSplitPosAmount('');
        setSurchargeCollected(0)

        // RESET KEYS ON MODAL CLOSE TO PREVENT STALE IDEMPOTENCY
        setPendingIdempotencyKey(null);
        setIsPendingCheckout(false);
        setBillIdempotencyKey('');

        setBillIdempotencyKey('');
        setPendingIdempotencyKey(null);
        setIsPendingCheckout(false);
        setPreferredTerminalBankAccountId(null);

        setOverrideTransactionDate(false);
        setTransactionDate('');
    };


    const finalizeSale = useCallback(async (paymentMethod, amountPaid, finalChangeDue, transactionReference = 'N/A', dueDate = null, splitPayments = null, forcedDepositUsed = null) => {

        setIsFinalizingPayment(true);
        try {
            if (!selectedCustomer) {
                showToast("No customer selected for sale finalization.", 'error');
                return;
            }
            if (!selectedStore) {
                showToast("No store selected for sale finalization.", 'error');
                return;
            }
            if (requiresManagerApproval) {
                showToast("Manager approval is required to finalize this order due to applied discounts.", "error");
                return;
            }
            const currencyCode = selectedStore?.setccurr || (cart.length > 0 ? cart[0].currencyCode : "USD");
            // const idempotencyKey = isPendingCheckout ? pendingIdempotencyKey : generateUniquePoNumber();
            // const idempotencyKey = isPendingCheckout && pendingIdempotencyKey
            //     ? pendingIdempotencyKey
            //     : getOrCreateIdempotencyKey(billIdempotencyKey);

            let idempotencyKey;
            if (isPendingCheckout && pendingIdempotencyKey) {
                // Resume of a previously pended order — keep its key
                idempotencyKey = pendingIdempotencyKey;
            } else if (
                (paymentMethod === 'pos' || paymentMethod === 'split') &&
                billIdempotencyKey
            ) {
                // Terminal / terminal-split already allocated a key during push — reuse it
                idempotencyKey = billIdempotencyKey;
            } else {
                // Cash, card, transfer, credit, pure deposit → always brand-new
                idempotencyKey = generateUniquePoNumber();
            }

            // const depositAmountToUse = forcedDepositUsed !== null
            //     ? forcedDepositUsed : (useDeposit ? Math.min(customerDeposit, grandTotal) : 0)

            const depositAmountToUse = forcedDepositUsed !== null
                ? Math.max(0, forcedDepositUsed)
                : (useDeposit ? Math.min(Math.max(0, customerDeposit), grandTotal) : 0);
            const finalAmountDue = grandTotal - depositAmountToUse;

            // const depositAmountToUse = useDeposit ? Math.min(customerDeposit, grandTotal) : 0;
            // const finalAmountDue = grandTotal - depositAmountToUse;

            const salesPayload = {
                member_id: user.users_id,
                customer_id: selectedCustomer.member_id,
                payment_method: paymentMethod,
                // total_amount: grandTotal,
                total_amount: finalAmountDue,
                amount_paid: paymentMethod === 'pos' ? posPaidAmount : amountPaid,
                // ----- NEW POS TERMINAL FIELDS -----
                pos_paid_amount: posPaidAmount,
                pos_surcharge: surchargeCollected,
                pos_transaction_ref: posTransactionRef,
                // -----------------------------------
                deposit_used: Number(depositAmountToUse) || 0,
                change_due: finalChangeDue,
                idempotency_key: idempotencyKey,
                store_id: selectedStore.store_id,
                currency: currencyCode,
                totaltax: totalTaxAmount,
                subtotal: calculateSubtotal,
                totaldiscount: totalDiscountAmount,
                items: cart.map(item => ({
                    catentry_id: item.id,
                    quantity: item.quantity,
                    price: item.price,
                    productname: item.name,
                    tax_breakdown: (item.tax_categories || []).map(tax => ({
                        taxcategory_id: tax.taxcategory_id,
                        code: tax.code || tax.identifier,
                        rate: tax.rate,
                        // amount: (item.price * item.quantity * tax.rate) / 100
                        amount: roundMoney(lineAmount(item.price, item.quantity) * tax.rate / 100)
                    }))
                })),
                applied_discounts: calculatedDiscounts,
                payment_due_date: paymentMethod === 'credit' ? dueDate : null,
                bank_account_id: paymentMethod === 'transfer' || paymentMethod === 'card' ? transactionReference : null,
                split_payments: splitPayments,
                transaction_date:
                    overrideTransactionDate && transactionDate
                        ? (() => {
                            const d = new Date(transactionDate);
                            return Number.isNaN(d.getTime())
                                ? null
                                : d.toISOString().slice(0, 19).replace('T', ' ');
                        })()
                        : null,
                // transaction_date: overrideTransactionDate && transactionDate
                //     ? new Date(transactionDate).toISOString().slice(0, 19).replace('T', ' ')   // backend expects datetime
                //     : null,
            };

            const hasCredit =
                selectedPaymentMethod === 'credit' ||
                (selectedPaymentMethod === 'split' && parseFloat(splitCreditAmount || '0') > 0);

            const endpoint =
                requiresCreditApproval && hasCredit
                    ? '/sales/submit-credit-approval'
                    : '/sales/finalize';

            // console.log("Payload being sent:", salesPayload);

            // const response = await client.postData('/sales/finalize', salesPayload);
            const response = await client.postData(endpoint, salesPayload);

            if (requiresCreditApproval && hasCredit) {
                showToast(response?.msg || 'Credit sale submitted for approval', 'success');
                // clear cart / close modal / do NOT treat as completed receipt
                closeCheckoutModal();
                setCart([]);
                setSelectedCustomer(null);
                setSplitCashAmount('');
                setSplitCardAmount('');
                setSplitTransferAmount('');
                setSplitCreditAmount('');
                setSelectedTransferBankAccount('');
                setSelectedCardBankAccount('');
                setCouponCodes([]);
                setPosPaidAmount(0);
                setPosTransactionRef(null);
                setPosPaymentStatus(null);
                setProductOffset(0);
                setRefreshProductsTrigger(prev => prev + 1);
                return;
            }

            showToast(response.msg || "Sale finalized successfully!", 'success');
            const receiptItems = cart.map(item => ({
                name: item.name,
                quantity: item.quantity,
                pricePerUnit: item.price,
                totalPrice: lineAmount(item.price, item.quantity),
                // totalPrice: item.price * item.quantity,
                priceSymbol: item.priceSymbol
            }));

            setReceiptData({
                orderId: idempotencyKey,
                shopName: selectedStore.identifier,
                cashierName: cashierFullName,
                customerInfo: {
                    name: `${selectedCustomer.user.first_name} ${selectedCustomer.user.middle_name} ${selectedCustomer.user.last_name}`,
                    contact: selectedCustomer.user.credential.logon_id,
                },
                orderItems: receiptItems,
                subtotal: calculateSubtotal,
                taxAmount: totalTaxAmount,
                taxes: cart.flatMap(item =>
                    (item.tax_categories || []).map(tax => ({
                        tax_identifier: tax.code || tax.identifier || `Tax ${tax.taxcategory_id}`,
                        rate: tax.rate,
                        // amount: (item.price * item.quantity * item.rate) / 100
                        amount: roundMoney(lineAmount(item.price, item.quantity) * tax.rate / 100)
                    }))
                )

                    .reduce((acc, tax) => {
                        const existing = acc.find(t => t.tax_identifier === tax.tax_identifier);
                        if (existing) {
                            existing.amount += tax.amount
                        } else {
                            acc.push(tax)
                        }
                        return acc
                    }, [])
                    .map(tax => ({
                        tax_identifier: `${tax.tax_identifier} (${tax.rate}%)`,
                        tax_amount: tax.amount
                    })),
                totalDiscount: totalDiscountAmount,
                finalAmountDue: grandTotal,
                depositUsed: depositAmountToUse,
                totalPaid: amountPaid + depositAmountToUse,
                changeDue: finalChangeDue,
                paymentStatus: paymentMethod,
                transactionReference: transactionReference,
                date: new Date().toLocaleString(),
                currencySymbol: currencyCodeToSymbolMap[currencyCode] || currencyCode,
                currencyCode: currencyCode,
                paymentDueDate: dueDate,
                splitPayments: splitPayments,
                footerMessage: 'Thank you for your purchase! Please come again. All sales final.'
            });

            depositCache.current.delete(selectedCustomer.member_id);
            setCustomerDeposit(0);
            setUseDeposit(false);

            setShowReceiptInvoiceModal(true);

            setIsPendingCheckout(false);
            setPendingIdempotencyKey(null);

            setCart([]);
            setShowCheckoutModal(false);
            setTransactionDate('');
            setPaymentAmount('');
            setChangeDue(0);
            setBillIdempotencyKey('');
            setPendingIdempotencyKey(null);
            setIsPendingCheckout(false);
            setSelectedPaymentMethod('');
            setSelectedCustomer(null);
            setPaymentDueDate('');
            setSplitCashAmount('');
            setSplitCardAmount('');
            setSplitTransferAmount('');
            setSplitCreditAmount('');
            setSelectedTransferBankAccount('');
            setSelectedCardBankAccount('');
            // setCouponCodeInput('');
            setCouponCodes([]);
            setProductOffset(0);
            setRefreshProductsTrigger(prev => prev + 1);
            fetchPendingOrders();
        } catch (err) {
            console.error("Error finalizing sale:", err);
            const errorMessage = err.response?.data?.detail || `Failed to finalize sale: ${err.message || err}`;
            showToast(errorMessage, 'error');
        } finally {
            setIsFinalizingPayment(false);
        }
    }, [
        user,
        selectedCustomer,
        selectedStore,
        cart,
        showToast,
        cashierFullName,
        totalTaxAmount,
        calculateSubtotal,
        calculatedDiscounts,
        totalDiscountAmount,
        grandTotal,
        requiresManagerApproval,
        posPaidAmount,
        posTransactionRef,
        surchargeCollected,
        fetchPendingOrders,
        overrideTransactionDate,
        transactionDate,
        useDeposit,
        customerDeposit,
        billIdempotencyKey,
        isPendingCheckout,
        pendingIdempotencyKey,
        requiresCreditApproval,
        closeCheckoutModal,
    ]);


    const splitTotal = useMemo(() => {
        const cash = parseFloat(splitCashAmount) || 0;
        const bank = bankSplits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0);
        const credit = parseFloat(splitCreditAmount) || 0;

        // confirmed terminal money that counts toward the SALE
        const confirmedPos = posPaidAmount || 0;

        // Typed but not-yet-pushed terminal amount (also toward the SALE)
        // only count it while nothing has been successfully collected yet for this leg
        const pendingPos =
            posPaidAmount > 0 ? 0 : (parseFloat(splitPosAmount) || 0);

        // const pos = posPaidAmount || 0;
        // return Math.round((cash + bank + credit + pos) * 100) / 100;
        return Math.round((cash + bank + credit + confirmedPos + pendingPos) * 100) / 100;
    }, [splitCashAmount, bankSplits, splitCreditAmount, posPaidAmount, splitPosAmount]);

    const handleFinalizePayment = useCallback(async () => {
        if (!depositCoversAll && !selectedPaymentMethod) {
            showToast("Please select a payment method.", 'error');
            return;
        }
        if (!selectedCustomer) {
            showToast("Please select a customer.", 'error');
            return;
        }
        if (requiresManagerApproval) {
            showToast("Manager approval is required to finalize this order due to applied discounts.", "error");
            return;
        }

        setIsFinalizingPayment(true)

        try {
            // always compute deposit usage using current values (no stale closure risks)
            const currentDepositUsed = useDeposit ? Math.min(customerDeposit, grandTotal) : 0;
            const currentRemainingDue = Math.max(0, grandTotal - currentDepositUsed);

            if (depositCoversAll) {
                // const depositAmountToUseNow = Math.min(customerDeposit, grandTotal);
                await finalizeSale(
                    'deposit',          // <- new / special method name
                    0,                  // amountPaid = 0
                    0,                  // changeDue = 0
                    'N/A',              // transactionReference
                    null,               // dueDate
                    null,               // splitPayments
                    currentDepositUsed,
                );
                return;
            }
            if (selectedPaymentMethod === 'pos') {
                const amountNeeded = useDeposit ? remainingDue : grandTotal;
                if (posPaidAmount + 0.001 < remainingDue) { // tolerance for float noise
                    showToast("POS terminal payment amount is less than remaining due.", 'error');
                    return;
                }
                await finalizeSale(
                    'pos',
                    posPaidAmount,
                    0, // change due is 0 for POS card/terminal transactions
                    posTransactionRef || 'POS-TERMINAL',
                    null,
                    null,
                    currentDepositUsed
                );
                return;
            }
            if (selectedPaymentMethod === 'split') {

                // const calculatedSplitTotal = Math.round(
                //     (parseFloat(splitCashAmount) || 0) +
                //     bankSplits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0) +
                //     (parseFloat(splitCreditAmount) || 0)
                //     * 100) / 100;

                const calculatedSplitTotal =
                    (parseFloat(splitCashAmount) || 0) +
                    bankSplits.reduce((sum, s) => sum + (parseFloat(s.amount) || 0), 0) +
                    (parseFloat(splitCreditAmount) || 0) +
                    (posPaidAmount || 0);


                const roundedTotal = Math.round(calculatedSplitTotal * 100) / 100;

                // if (Math.abs(calculatedSplitTotal - grandTotal) > 0.01) {
                if (Math.abs(roundedTotal - currentRemainingDue) > 0.01) {
                    // showToast(`Total split amount (${calculatedSplitTotal.toFixed(2)}) does not match grand total (${grandTotal.toFixed(2)}).`, 'error');
                    showToast(`Total split amount (${roundedTotal.toFixed(2)}) does not match remaining due (${currentRemainingDue.toFixed(2)}).`, 'error');
                    return;
                }

                // Validate bank splits
                for (const split of bankSplits) {
                    if ((parseFloat(split.amount) || 0) > 0 && !split.bankAccountId) {
                        showToast("Please select a bank account for every bank payment with an amount.", 'error');
                        return;
                    }
                }

                if (splitCreditAmount && (!customerLOC || !customerLOC.is_active || parseFloat(splitCreditAmount) > availableCredit)) {
                    showToast("Cannot finalize: The credit amount exceeds the customer's available credit.", 'error');
                    return;
                }

                const splitPayments = {};
                if (parseFloat(splitCashAmount) > 0) splitPayments.cash = parseFloat(splitCashAmount);

                bankSplits.forEach(split => {
                    const amount = parseFloat(split.amount);
                    if (amount > 0) {
                        const type = split.paymentType;
                        splitPayments[type] = splitPayments[type] || [];
                        splitPayments[type].push({
                            amount,
                            bank_account_id: split.bankAccountId
                        });
                    }
                });

                if (parseFloat(splitCreditAmount) > 0) splitPayments.credit = parseFloat(splitCreditAmount);

                // await finalizeSale(
                //     selectedPaymentMethod,
                //     depositCoversAll ? 0 : parseFloat(paymentAmount),
                //     depositCoversAll ? 0 : changeDue,
                //     'split', grandTotal, 0, 'N/A', null, splitPayments
                // );
                await finalizeSale(
                    'split',
                    0,
                    0,
                    'N/A',
                    null,
                    splitPayments,
                    currentDepositUsed,
                )
                return;
            }

            if (selectedPaymentMethod === 'transfer') {
                // if (parseFloat(paymentAmount) < remainingDue) {
                //     showToast("Payment amount is less than remaining due.", 'error');
                //     return;
                // }
                if (parseFloat(paymentAmount) + 0.005 < remainingDue) {
                    showToast('Insufficient amount paid.', 'error');
                    return;
                }
                if (!selectedTransferBankAccount) {
                    showToast("Please select a bank account.", 'error');
                    return;
                }
                await finalizeSale(selectedPaymentMethod, parseFloat(paymentAmount), changeDue, selectedTransferBankAccount, null, null, currentDepositUsed);
                return;
            }
            if (selectedPaymentMethod === 'card') {
                if (parseFloat(paymentAmount) < remainingDue) {
                    showToast("Payment amount is less than remaining due.", 'error');
                    return;
                }
                if (!selectedCardBankAccount) {
                    showToast("Please select a bank account.", 'error');
                    return;
                }
                await finalizeSale(selectedPaymentMethod, parseFloat(paymentAmount), changeDue, selectedCardBankAccount, null, null, currentDepositUsed);
                return;
            }
            if (selectedPaymentMethod === 'cash') {
                if (parseFloat(paymentAmount) < remainingDue) {
                    showToast("Payment amount is less than remaining due.", 'error');
                    return;
                }
                await finalizeSale(selectedPaymentMethod, parseFloat(paymentAmount), changeDue, null, null, null, currentDepositUsed);
                return;
            }
            if (selectedPaymentMethod === 'credit') {
                if (!customerLOC || !customerLOC.is_active) {
                    showToast("Credit line is inactive or not available for this customer.", "error");
                    return;
                }
                if (remainingDue > availableCredit) {
                    showToast("Insufficient credit limit for this transaction.", "error");
                    return;
                }
                if (!paymentDueDate) {
                    showToast("Please select a payment due date for credit.", "error");
                    return;
                }
                await finalizeSale('credit', parseFloat(paymentAmount), 0, 'N/A', paymentDueDate, null, currentDepositUsed);
                return;
            }

            showToast("Invalid payment configuration", "error");

        } catch (error) {
            console.error("Payment finalization failed:", error)
            showToast(`Payment finalization failed ${error.message || error.msg || error.response.data.msg}`, "error")
        } finally {
            setIsFinalizingPayment(false);
        }
    }, [
        depositCoversAll,
        selectedPaymentMethod,
        selectedCustomer,
        requiresManagerApproval,
        customerDeposit,
        paymentAmount,
        grandTotal,
        changeDue,
        showToast,
        finalizeSale,
        customerLOC,
        paymentDueDate,
        selectedTransferBankAccount,
        selectedCardBankAccount,
        splitCashAmount,
        splitCreditAmount,
        availableCredit,
        bankSplits,
        splitTotal,
        splitCardAmount,
        splitTransferAmount,
        posPaidAmount,
        remainingDue,
        useDeposit,
        posTransactionRef,
    ]);

    const currentCurrencySymbol = useMemo(() => {
        if (selectedStore && selectedStore.setccurr) {
            return currencyCodeToSymbolMap[selectedStore.setccurr] || selectedStore.setccurr;
        }
        if (cart.length > 0) {
            return cart[0].priceSymbol;
        }
        return '$';
    }, [selectedStore, cart]);

    // const splitRemaining = useMemo(() => {
    //     return grandTotal - splitTotal;
    // }, [grandTotal, splitTotal]);

    const splitRemaining = useMemo(() => {
        const effectiveDue = useDeposit ? remainingDue : grandTotal;
        return effectiveDue - splitTotal;
    }, [useDeposit, remainingDue, grandTotal, splitTotal]);

    const discountOptions = useMemo(() => {
        return availableDiscountRules.map(rule => ({
            label: rule.coupon_code ? `${rule.name} (${rule.coupon_code})` : rule.name,
            value: rule.requires_coupon_code ? rule.coupon_code : '' + rule.discountrule_id
        }));
    }, [availableDiscountRules]);

    // useEffect(() => {
    //     if (!showCheckoutModal) return;

    //     if (useDeposit) {
    //         setPaymentAmount(remainingDue.toFixed(2));
    //         setChangeDue(0); // or let cashier overpay if wanted
    //     } else if (!useDeposit) {
    //         setPaymentAmount(grandTotal.toFixed(2));
    //         setChangeDue(0);
    //     }
    // }, [showCheckoutModal, useDeposit, remainingDue, grandTotal]);
    useEffect(() => {
        if (!showCheckoutModal) return;

        const raw = useDeposit ? remainingDue : grandTotal;
        const rounded = Math.round(raw * 100) / 100;
        setPaymentAmount(rounded.toFixed(2));
        setChangeDue(0);
    }, [showCheckoutModal, useDeposit, remainingDue, grandTotal]);

    // useEffect(() => {
    //     if (selectedPaymentMethod == 'split' && remainingDue > 0) {
    //         if (!splitCashAmount && !bankSplits.some(s => parseFloat(s.amount) > 0)) {
    //             setSplitCashAmount(remainingDue.toFixed(2))
    //         }
    //     }
    // }, [selectedPaymentMethod, remainingDue]);

    const AMOUNT_EPS = 0.01;

    // const canFinalize =
    //     !isFinalizingPayment &&
    //     selectedCustomer &&
    //     !requiresManagerApproval &&
    //     (depositCoversAll ||
    //         posPaidAmount >= currentNetPayable ||
    //         (selectedPaymentMethod &&
    //             ((selectedPaymentMethod === 'pos' && posPaidAmount > 0) ||
    //                 (selectedPaymentMethod === 'split' && Math.abs(splitTotal - remainingDue) < 0.01) ||
    //                 (selectedPaymentMethod !== 'split' && selectedPaymentMethod !== 'credit' && parseFloat(paymentAmount) >= remainingDue) ||
    //                 (selectedPaymentMethod === 'credit' && paymentDueDate && remainingDue <= availableCredit))));
    const canFinalize =
        !isFinalizingPayment &&
        selectedCustomer &&
        !requiresManagerApproval &&
        (depositCoversAll ||
            posPaidAmount + AMOUNT_EPS >= currentNetPayable ||
            (selectedPaymentMethod &&
                (
                    (selectedPaymentMethod === 'pos' && posPaidAmount + AMOUNT_EPS >= remainingDue) ||
                    (selectedPaymentMethod === 'split' && Math.abs(splitTotal - remainingDue) < AMOUNT_EPS) ||
                    (selectedPaymentMethod === 'cash' &&
                        parseFloat(paymentAmount || 0) + AMOUNT_EPS >= remainingDue) ||
                    (selectedPaymentMethod === 'card' &&
                        parseFloat(paymentAmount || 0) + AMOUNT_EPS >= remainingDue &&
                        !!selectedCardBankAccount) ||
                    (selectedPaymentMethod === 'transfer' &&
                        parseFloat(paymentAmount || 0) + AMOUNT_EPS >= remainingDue &&
                        !!selectedTransferBankAccount) ||
                    (selectedPaymentMethod === 'credit' &&
                        paymentDueDate &&
                        remainingDue <= availableCredit + AMOUNT_EPS)
                )));

    // --- DEBUG: finalize gate (remove after fix) ---
    useEffect(() => {
        if (!showCheckoutModal) return;
        const paid = parseFloat(paymentAmount);
        // console.log('[canFinalize debug]', {
        //     selectedPaymentMethod,
        //     paymentAmount,
        //     paidNum: paid,
        //     remainingDue,
        //     amountOk: paid >= remainingDue,
        //     selectedTransferBankAccount,
        //     selectedCardBankAccount,
        //     preferredTerminalBankAccountId,
        //     bankOptionValues: bankAccountOptions.map(o => o.value),
        //     preferredInOptions: preferredTerminalBankAccountId
        //         ? bankAccountOptions.some(o => o.value === String(preferredTerminalBankAccountId))
        //         : null,
        //     depositCoversAll,
        //     requiresManagerApproval,
        //     isFinalizingPayment,
        //     selectedCustomer: !!selectedCustomer,
        // });
    }, [
        showCheckoutModal,
        selectedPaymentMethod,
        paymentAmount,
        remainingDue,
        selectedTransferBankAccount,
        selectedCardBankAccount,
        preferredTerminalBankAccountId,
        bankAccountOptions,
        depositCoversAll,
        requiresManagerApproval,
        isFinalizingPayment,
        selectedCustomer,
    ]);

    if (!user) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to use the Point of Sale.</p>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-[calc(100vh-100px)] bg-gray-100 p-6 rounded-lg shadow-inner gap-6">
            <div className="flex flex-col bg-white rounded-lg shadow-md p-6 w-full flex-1 min-h-0">


                {/* <div className="flex flex-col lg:flex-row gap-4 mb-6 items-start lg:items-center"> */}
                {/* UGLY mobile-STACKED STORE SELCTOR + SEARCH BAR + PENDING ORDERS SYSTEM starts here */}
                {/* <div className="flex flex-col sm:flex-row gap-4 mb-6 items-start sm:items-center"> */}
                {/* Store Selector */}
                {/* <div className="relative mb-4">
                        <Button
                            variant="secondary"
                            size="md"
                            onClick={() => setShowStoreSelector(!showStoreSelector)}
                            className="flex py-3 items-center gap-2 shadow-sm whitespace-nowrap min-w-[180px]"
                        >
                            <Package size={20} />
                            {selectedStore ? selectedStore.identifier : 'Select Store'}
                            <ChevronDown size={18} className={`transition-transform ${showStoreSelector ? 'rotate-180' : ''}`} />
                        </Button>
                        {showStoreSelector && (
                            <div className="absolute top-12 left-0 w-72 bg-white rounded-lg shadow-xl border border-gray-200 p-4 z-50">
                                {loadingStores ? (
                                    <div className="flex items-center justify-center py-4">
                                        <Loader2 className="animate-spin h-5 w-5 mr-2" />
                                        <span>Loading stores...</span>
                                    </div>
                                ) : errorStores ? (
                                    <p className="text-red-600 text-sm">{errorStores}</p>
                                ) : stores.length === 0 ? (
                                    <p className="text-red-600 text-sm">No stores available</p>
                                ) : (
                                    <div className="space-y-2 max-h-60 overflow-y-auto">
                                        {stores.map(store => (
                                            <button
                                                key={store.value}
                                                onClick={() => {
                                                    if (selectedStore?.store_id !== store.value) {
                                                        setSelectedStore(store.storeData);
                                                        setCart([]);
                                                        setProductOffset(0);
                                                        setProducts([]);
                                                        // setCouponCodeInput('');
                                                        setCouponCodes([]);
                                                        setRefreshProductsTrigger(prev => prev + 1);
                                                    }
                                                    setShowStoreSelector(false);
                                                }}
                                                className={`w-full text-left px-3 py-2 rounded-md transition-colors ${selectedStore?.store_id === store.value
                                                    ? 'bg-gmail-blue text-white'
                                                    : 'hover:bg-gray-100'
                                                    }`}
                                            >
                                                {store.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div> */}

                {/* Search Bar - takes remaining space */}
                {/* <div className="flex-1 min-w-0">
                        <div className="relative">
                            <Input
                                type="text"
                                placeholder="Search products..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-10 py-3 text-base rounded-lg bg-gray-50 border border-gray-300 focus:border-gmail-blue"
                                disabled={!selectedStore}
                            />
                            <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                                    aria-label="Clear search"
                                >
                                    <X size={20} />
                                </button>
                            )}
                        </div>
                    </div> */}

                {/* Pending Orders Button - same size as Store Selector */}
                {/* <Button
                        variant="secondary"
                        size="md"
                        onClick={() => setShowPendingOrdersModal(true)}
                        className="relative flex py-3 items-center gap-2 shadow-sm whitespace-nowrap min-w-[180px] mb-4"
                        disabled={loadingPendingOrders || !selectedStore}
                    >
                        <Bell size={20} />
                        Pending Orders
                        {pendingOrders.length > 0 && (
                            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                                {pendingOrders.length}
                            </span>
                        )}
                    </Button> */}
                {/* </div> */}
                {/* UGLY mobile-STACKED STORE SELCTOR + SEARCH BAR + PENDING ORDERS SYSTEM ends here */}

                {/* <div className="flex items-center justify-between gap-2 p-4 bg-white border-b border-gray-200"> */}
                <div className="flex items-center justify-between gap-2 p-4 bg-white border-gray-200">
                    {/* Condition to completely hide other controls on mobile if the search bar is expanded */}
                    {!isMobileSearchOpen && (
                        <>
                            {/* Store Selection Dropdown */}
                            <div className="relative flex shrink-0">
                                <Button
                                    variant="outline"
                                    onClick={() => setShowStoreSelector(!showStoreSelector)}
                                    className="flex items-center gap-1.5 px-2 py-2 sm:px-4 sm:py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none"
                                >
                                    <Package size={18} className="text-gray-500" />
                                    {/* Hide store name on mobile, show on desktop */}
                                    <span className="hidden sm:inline max-w-[150px] truncate">
                                        {selectedStore ? selectedStore.identifier : 'Select Store'}
                                    </span>
                                    <ChevronDown size={16} className="text-gray-400" />
                                </Button>

                                {showStoreSelector && (
                                    <div className="absolute left-0 z-50 w-56 mt-2 origin-top-left bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                        <div className="py-1 max-h-60 overflow-y-auto">
                                            {stores.map((storeOption) => (
                                                <button
                                                    key={storeOption.value}
                                                    onClick={() => {
                                                        setSelectedStore(storeOption.storeData);
                                                        setProducts([]);
                                                        setProductOffset(0);
                                                        setShowStoreSelector(false);
                                                    }}
                                                    className={`w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 ${selectedStore?.store_id === storeOption.value ? 'bg-indigo-50 font-semibold' : ''
                                                        }`}
                                                >
                                                    {storeOption.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* Search Input Box */}
                    <div className={`flex-1 flex items-center justify-start ${isMobileSearchOpen ? 'w-full' : ''}`}>
                        <div className={`relative ${isMobileSearchOpen ? 'w-full flex items-center gap-2' : 'hidden md:block flex-1 max-w-auto'}`}>
                            <div className="relative flex-1">
                                {/* <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                </span> */}
                                <Input
                                    type="text"
                                    placeholder="Search product, category..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md bg-gray-50 focus:bg-white text-sm"
                                    containerClassName="!mb-0"
                                    disabled={!selectedStore}
                                />
                                <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
                                {searchTerm && (
                                    <button
                                        onClick={() => setSearchTerm('')}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700" aria-label="Clear search"
                                    >
                                        <X size={20} />
                                    </button>
                                )}
                            </div>
                            {isMobileSearchOpen && (
                                <button
                                    onClick={() => {
                                        setIsMobileSearchOpen(false);
                                        setSearchTerm('');
                                    }}
                                    className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-md"
                                >
                                    <X size={18} />
                                </button>
                            )}
                        </div>

                        {/* Mobile Search Trigger Button */}
                        <div className="md:hidden">
                            {!isMobileSearchOpen ? (
                                <Button
                                    variant="outline"
                                    onClick={() => setIsMobileSearchOpen(true)}
                                    className="p-2 border border-gray-300 rounded-md bg-white text-gray-700 hover:bg-gray-50"
                                >
                                    <Search size={18} className="text-gray-500" />
                                </Button>
                            ) : null}
                        </div>
                    </div>

                    {/* Pending Orders Control */}
                    {!isMobileSearchOpen && (
                        <div className="flex items-center flex-shrink-0">
                            <Button
                                variant="outline"
                                onClick={() => setShowPendingOrdersModal(true)}
                                className="relative flex items-center gap-1.5 px-2 py-2 sm:px-4 sm:py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none"
                                disabled={loadingPendingOrders || !selectedStore}
                            >
                                <Bell size={18} className="text-gray-500" />
                                {/* Text hidden on mobile, visible on desktop */}
                                <span className="hidden sm:inline">Pending Orders</span>

                                {/* Desktop badge layout */}
                                {pendingOrders.length > 0 && (
                                    <span className="hidden sm:inline-flex items-center justify-center px-2 py-0.5 ml-1 text-xs font-bold leading-none text-red-100 bg-red-600 rounded-full">
                                        {pendingOrders.length}
                                    </span>
                                )}

                                {/* Mobile badge indicator: Small notification dot over the bell */}
                                {pendingOrders.length > 0 && (
                                    <span className="absolute top-1.5 right-1.5 block sm:hidden h-2.5 w-2.5 rounded-full bg-red-600 ring-2 ring-white" />
                                )}
                            </Button>
                        </div>
                    )}
                </div>

                {/* Click outside to close store dropdown */}
                {showStoreSelector && (
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowStoreSelector(false)}
                    />
                )}

                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    {loadingProducts && products.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-text-secondary">
                            <Loader2 className="animate-spin h-8 w-8 mb-3 text-gmail-blue" />
                            <p>Loading products...</p>
                        </div>
                    ) : errorProducts ? (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
                            <p>{errorProducts}</p>
                        </div>
                    ) : filteredProducts.length === 0 ? (
                        <div className="p-8 text-center text-text-secondary">
                            <Info size={48} className="mx-auto mb-4 text-gray-400" />
                            <p className="text-lg">No products found for this store or matching your search.</p>
                            <p className="text-sm mt-2">Please ensure products are assigned to this store or adjust your search.</p>
                            <Button onClick={() => navigate('/catalog/create-item')} variant="secondary" className="mt-4">
                                Add New Product Item
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                {filteredProducts.map(product => (
                                    <ProductCard
                                        key={product.id}
                                        product={product}
                                        cart={cart}
                                        onAddToCart={addToCart}
                                        onUpdateQuantity={updateCartQuantity}
                                        onDirectQuantityChange={handleQuantityChange}
                                        onFinalizeQuantity={finalizeQuantity}
                                    />
                                ))}
                            </div>
                            {hasMoreProducts && (
                                <div className="flex justify-center mt-4">
                                    <Button
                                        onClick={() => setProductOffset(prev => prev + productLimit)}
                                        disabled={loadingProducts}
                                        variant="secondary"
                                    >
                                        {loadingProducts ? 'Loading More...' : 'Load More Products'}
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {cart.length > 0 && (
                <button
                    onClick={() => setShowCartModal(true)}
                    className="fixed bottom-6 right-6 z-50 flex h-20 w-20 items-center justify-center rounded-full bg-gmail-blue text-white shadow-2xl hover:bg-blue-700 transition-all"
                    aria-label="Open Cart"
                >
                    <ShoppingCart size={24} />
                    <span className="absolute -right-1 -top-1 flex h-8 w-8 items-center justify-center rounded-full bg-red-500 text-sm font-bold text-white">
                        {cart.length}
                    </span>
                </button>
            )}
            {/* Cart Modal for non-desktop */}
            <Modal
                isOpen={showCartModal}
                onClose={() => setShowCartModal(false)}
                title={`Cart (${cart.length} item${cart.length !== 1 ? 's' : ''})`}
                size="wide"
                overlayClassName="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-end justify-center"
                contentClassName="bg-white rounded-t-3xl shadow-2xl w-full max-h-[90vh] flex flex-col"
            >
                <div className="flex-1 overflow-y-auto p-6">
                    { /* ! REPEATED TODO: factor this out to a reusable component */}
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-2xl font-bold text-text-primary flex items-center gap-2">
                            <ShoppingCart size={24} className="text-gmail-green" /> Current Cart
                        </h2>
                        {selectedStore && (
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setShowPendingOrdersModal(true)}
                                className="relative flex items-center gap-2"
                                disabled={loadingPendingOrders}
                            >
                                <Bell size={20} />
                                {pendingOrders.length > 0 && (
                                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                                        {pendingOrders.length}
                                    </span>
                                )}
                                Pending Orders
                            </Button>
                        )}
                    </div>
                    <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar border border-gray-200 rounded-lg p-2 mb-4">
                        {cart.length === 0 ? (
                            <div className="text-center text-text-secondary py-8">
                                <Info size={32} className="mx-auto mb-2 text-gray-400" />
                                <p>Your cart is empty.</p>
                            </div>
                        ) : (
                            <>
                                <table className="min-w-full text-sm text-text-primary">
                                    <thead>
                                        <tr>
                                            <th className="px-2 py-2 text-left font-semibold text-gray-700">Item</th>
                                            <th className="px-2 py-2 text-left font-semibold text-gray-700">Price</th>
                                            <th className="px-2 py-2 text-left font-semibold text-gray-700">Qty</th>
                                            <th className="px-2 py-2 text-left font-semibold text-gray-700">Total</th>
                                            <th className="px-2 py-2 text-right font-semibold text-gray-700">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cart.map(item => (
                                            <tr key={item.id} className="border-b border-gray-100 last:border-b-0">
                                                <td className="px-2 py-2">{item.name}</td>
                                                {/* <td className="px-2 py-2">{item.priceSymbol}{parseFloat(item.price).toFixed(2)}</td> */}
                                                <td className="px-2 py-2">
                                                    {item.priceSymbol}{lineAmount(item.price, item.quantity).toFixed(2)}
                                                </td>
                                                <td className="px-2 py-2">
                                                    <div className="flex items-center gap-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => updateCartQuantity(item.id, -1)}
                                                            className="p-1.5"
                                                        >
                                                            <Minus size={14} />
                                                        </Button>
                                                        <Input
                                                            type="number"
                                                            value={item.quantity}
                                                            onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                                                            onBlur={() => finalizeQuantity(item.id)}
                                                            className="w-16 text-center text-sm px-1 py-0.5 transition-all duration-200 focus:w-24 focus:ring-2 focus:ring-blue-500"
                                                            min="0"
                                                            step="0.01"
                                                            placeholder="0.00" />
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => updateCartQuantity(item.id, 1)}
                                                            className="p-1.5"
                                                        >
                                                            <Plus size={14} />
                                                        </Button>
                                                    </div>
                                                </td>
                                                {/* <td className="px-2 py-2">
                                                    {item.priceSymbol}{(parseFloat(item.price) * item.quantity).toFixed(2)}
                                                </td> */}
                                                <td className="px-2 py-2">
                                                    {item.priceSymbol}{lineAmount(item.price, item.quantity).toFixed(2)}
                                                </td>
                                                <td className="px-2 py-2 text-right">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeFromCart(item.id)}
                                                        className="p-1.5 text-red-500 hover:bg-red-100"
                                                    >
                                                        <X size={14} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <Button variant="secondary" onClick={handleClearCart} className="w-full flex items-center justify-center mt-4">
                                    <Trash2 size={16} className="mr-2" /> Clear Cart
                                </Button>
                            </>
                        )}
                    </div>
                    <div className="mb-4">
                        <SearchableSelectBox
                            label="Select Customer"
                            id="customer-select"
                            name="customer-select"
                            value={selectedCustomer?.value || ''}
                            onChange={(e) => {
                                const selectedOption = customerOptions.find(opt => opt.value === parseInt(e.target.value));
                                setSelectedCustomer(selectedOption ? selectedOption.customerData : null);
                            }}
                            options={customerOptions}
                            placeholder="Search or select a customer"
                            onSearchChange={setSearchCustomerTerm}
                            loading={loadingCustomers}
                            error={customerError}
                            icon={<User size={18} />}
                        />
                        {selectedCustomer && (
                            <p className="text-sm text-gray-600 mt-2">
                                Selected: {selectedCustomer.user.first_name} {selectedCustomer.user.middle_name} {selectedCustomer.user.last_name}
                            </p>
                        )}
                        <p className="text-xs text-gmail-blue hover:underline cursor-pointer mt-2 text-right" onClick={() => setShowNewCustomerModal(true)}>
                            + Add New Customer
                        </p>
                    </div>
                    {/* <div className="mb-4">
                        <SearchableSelectBox
                            label="Select Discount (Optional)"
                            id="discount-select"
                            name="discount-select"
                            value={couponCodeInput}
                            onChange={(e) => setCouponCodeInput(e.target.value)}
                            options={[{ label: 'No Discount', value: '' }, ...discountOptions]}
                            placeholder="Search or select a discount"
                            icon={<Tag size={18} />}
                            disabled={!selectedStore || cart.length === 0}
                        />
                        {appliedCouponCodeStatus && (
                            <p className={`text-sm mt-1 flex items-center gap-1 ${appliedCouponCodeStatus === 'valid' ? 'text-green-600' : 'text-red-600'}`}>
                                {appliedCouponCodeStatus === 'valid' ? (
                                    <>
                                        <CheckCircle size={14} /> Discount Applied!
                                    </>
                                ) : (
                                    <>
                                        <XCircle size={14} /> {discountValidationMessage || 'Invalid Discount or Conditions Not Met.'}
                                    </>
                                )}
                            </p>
                        )}
                    </div> */}
                    {/* Multiple Discounts Section */}
                    <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-medium text-gray-700">
                                Applied Discounts
                            </label>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => setCouponCodes(prev => [...prev, ''])}
                                disabled={!selectedStore || cart.length === 0}
                                className="flex items-center justify-center"
                            >
                                <Plus size={16} className="mr-1" /> Add Discount
                            </Button>
                        </div>

                        {couponCodes.map((code, index) => (
                            <div key={index} className="flex gap-2 mb-3">
                                <div className="flex-1">
                                    <SearchableSelectBox
                                        label={index === 0 ? "Select Discount" : undefined}
                                        value={code}
                                        onChange={(e) => {
                                            const newCodes = [...couponCodes];
                                            newCodes[index] = e.target.value;
                                            setCouponCodes(newCodes);
                                        }}
                                        options={[
                                            { label: 'No Discount', value: '' },
                                            ...discountOptions
                                        ]}
                                        placeholder="Search or select a discount"
                                        icon={<Tag size={18} />}
                                        disabled={!selectedStore || cart.length === 0}
                                    />
                                </div>

                                {couponCodes.length > 1 && (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                            const newCodes = couponCodes.filter((_, i) => i !== index);
                                            setCouponCodes(newCodes);
                                        }}
                                        className="text-red-600 hover:bg-red-50 mt-6"
                                    >
                                        <X size={18} />
                                    </Button>
                                )}
                            </div>
                        ))}

                        {/* Status Messages */}
                        {appliedCouponCodeStatus.length > 0 && (
                            <div className="mt-2 space-y-1">
                                {appliedCouponCodeStatus.map((status, idx) => (
                                    status && (
                                        <p key={idx} className={`text-sm flex items-center gap-1 ${status === 'valid' ? 'text-green-600' : 'text-red-600'}`}>
                                            {status === 'valid' ? (
                                                <CheckCircle size={14} />
                                            ) : (
                                                <XCircle size={14} />
                                            )}
                                            {discountValidationMessage[idx] || (status === 'valid' ? 'Discount applied' : 'Invalid discount')}
                                        </p>
                                    )
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="border-t border-gray-200 pt-4">
                        <p className="text-xl font-bold text-right text-text-primary mb-2">
                            Subtotal: {currentCurrencySymbol}{calculateSubtotal.toFixed(2)}
                        </p>

                        {cart.some(item => (item.tax_categories || []).length > 0) && (
                            <div className="text-sm text-right text-text-secondary mb-2">
                                {cart.flatMap(item =>
                                    (item.tax_categories || []).map(tax => ({
                                        code: tax.code || tax.identifier,
                                        rate: tax.rate,
                                        // amount: (item.price * item.quantity * tax.rate) / 100
                                        amount: roundMoney(lineAmount(item.price, item.quantity) * tax.rate / 100)
                                    }))
                                )
                                    .reduce((acc, tax) => {
                                        const existing = acc.find(t => t.code === tax.code);
                                        if (existing) existing.amount += tax.amount;
                                        else acc.push(tax);
                                        return acc;
                                    }, [])
                                    .map((tax, idx) => (
                                        <p key={idx}>
                                            {tax.code}: {currentCurrencySymbol}{tax.amount.toFixed(2)}
                                        </p>
                                    ))}
                                <p className="text-xl font-bold text-text-primary mt-2">
                                    Total Tax: {currentCurrencySymbol}{totalTaxAmount.toFixed(2)}
                                </p>
                            </div>
                        )}
                        <p className="text-xl font-bold text-right text-text-primary mb-2">
                            Total Discount: <span className="text-red-600">- {currentCurrencySymbol}{totalDiscountAmount.toFixed(2)}</span>
                        </p>
                        <p className="text-2xl font-extrabold text-right text-gmail-blue mb-6">
                            Total: {currentCurrencySymbol}{grandTotal.toFixed(2)}
                        </p>
                        <div className="flex gap-4">
                            <Button
                                onClick={handleCheckout}
                                variant="primary"
                                size="lg"
                                className="flex-1 flex items-center justify-center gap-2"
                                disabled={cart.length === 0 || !selectedStore || requiresManagerApproval || !selectedCustomer}
                            >
                                <ShoppingBasket size={20} /> Checkout
                            </Button>
                            <Button
                                onClick={handleGenerateBill}
                                variant="secondary"
                                size="lg"
                                className="flex-1 flex items-center justify-center gap-2"
                                disabled={cart.length === 0 || !selectedStore || requiresManagerApproval || !selectedCustomer}
                            >
                                <PauseCircle size={20} /> Pend Order
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={showBillPreviewModal}
                onClose={() => setShowBillPreviewModal(false)}
                title="Bill Preview - Picking Slip"
                overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
                contentClassName="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-auto flex flex-col"
            >
                <div ref={receiptRef} className="receipt-content font-mono text-xs text-gray-900 leading-tight">
                    {/* Header */}
                    <div className="text-center mb-4">
                        <h3 className="font-bold text-base uppercase">{selectedStore?.identifier || 'Store'}</h3>
                        <p className="text-xs">Bill ID: {billIdempotencyKey}</p>
                        <p className="text-xs my-1">Date: {new Date().toLocaleString()}</p>
                        {cashierFullName && <p className="text-xs my-1">Cashier: {cashierFullName}</p>}
                        <div className="border-b-dashed border-gray-400 my-2"></div>
                    </div>

                    {/* Customer Info */}
                    {selectedCustomer && (
                        <div className="mb-4">
                            <p className="font-bold text-sm">Customer:</p>
                            <p className="text-xs">{selectedCustomer.user.first_name} {selectedCustomer.user.last_name}</p>
                            <div className="border-b-dashed border-gray-400 my-2"></div>
                        </div>
                    )}

                    {/* Items - NO PRICES */}
                    <div className="mb-4">
                        <div className="flex justify-between font-bold text-sm pb-1 border-b-dashed border-gray-400">
                            <span className="w-3/4 text-left">Item</span>
                            <span className="w-1/4 text-center">Qty</span>
                        </div>
                        {billPreviewItems.map((item, index) => (
                            <div key={index} className="flex justify-between py-1 border-b-dashed border-gray-200 last:border-b-0">
                                <span className="w-3/4 text-left">{item.name}</span>
                                <span className="w-1/4 text-center">{item.quantity}</span>
                            </div>
                        ))}
                        <div className="border-b-dashed border-gray-400 mt-2"></div>
                    </div>

                    {/* Footer */}
                    <div className="text-center text-xs">
                        <p className="font-bold mb-1">PICKING SLIP</p>
                        <p>Please verify items before dispatch.</p>
                        <p className="mt-2">Prices are hidden for security.</p>
                    </div>
                </div>

                {/* Print & Close Buttons */}
                <div className="flex justify-center gap-4 mt-6 no-print">
                    <Button
                        onClick={() => {
                            const printWindow = window.open('', '_blank');
                            if (!printWindow) {
                                showToast("Print blocked. Allow pop-ups.", 'error');
                                return;
                            }
                            const content = receiptRef.current?.innerHTML || '';
                            const fullHtml = `
                    <html>
                    <head>
                        <title>Bill - ${billIdempotencyKey}</title>
                        <script src="https://cdn.tailwindcss.com"></script>
                        <style>
                            @page { size: auto; margin: 5mm; }
                            body { font-family: monospace; font-size: 10px; padding: 5mm; line-height: 1.2; }
                            .border-b-dashed { border-bottom: 1px dashed #000; }
                            .border-t-dashed { border-top: 1px dashed #000; }
                            .text-center { text-align: center; }
                            .text-left { text-align: left; }
                            .font-bold { font-weight: bold; }
                            .text-xs { font-size: 10px; }
                            .text-sm { font-size: 12px; }
                            .text-base { font-size: 14px; }
                            .my-1 { margin: 4px 0; }
                            .py-1 { padding: 4px 0; }
                            .flex { display: flex; }
                            .justify-between { justify-content: space-between; }
                            .w-3\\/4 { width: 75%; }
                            .w-1\\/4 { width: 25%; }
                            @media print { .no-print { display: none; } }
                        </style>
                    </head>
                    <body><div style="max-width: 80mm; margin: 0 auto;">${content}</div></body>
                    </html>`;
                            printWindow.document.write(fullHtml);
                            printWindow.document.close();
                            printWindow.onload = () => setTimeout(() => { printWindow.print(); printWindow.close(); }, 200);
                        }}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md shadow"
                    >
                        Print Bill
                    </Button>
                    <Button
                        onClick={() => setShowBillPreviewModal(false)}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md shadow"
                    >
                        Close
                    </Button>
                </div>
            </Modal>

            <Modal
                isOpen={showNewCustomerModal}
                onClose={() => setShowNewCustomerModal(false)}
                title="Add New Customer"
                size="wide">
                <NewCustomer selectedStore={selectedStore} onSuccess={() => {
                    setShowNewCustomerModal(false);
                    fetchCustomers();
                }} />
            </Modal>
            <Modal
                size='wide'
                isOpen={showCheckoutModal}
                // onClose={() => setShowCheckoutModal(false)}
                onClose={closeCheckoutModal}
                title="Complete Sale"
            >
                <div className="p-6">
                    <p className="text-2xl font-bold text-center text-text-primary mb-2">
                        {/* Amount Due: {currentCurrencySymbol}{grandTotal.toFixed(2)} */}
                        Amount Due: {' '}
                        {useDeposit
                            ? `${currentCurrencySymbol}${Math.max(0, grandTotal - customerDeposit).toFixed(2)}`
                            : `${currentCurrencySymbol}${grandTotal.toFixed(2)}`}
                    </p>
                    {useDeposit && customerDeposit > 0 && (
                        <p className="text-sm text-center text-green-600 mb-4">
                            Deposit applied: -{currentCurrencySymbol}{Math.min(customerDeposit, grandTotal).toFixed(2)}
                        </p>
                    )}
                    <p className="text-sm text-center text-text-secondary mb-6">
                        (Subtotal: {currentCurrencySymbol}{calculateSubtotal.toFixed(2)} + Tax: {currentCurrencySymbol}{totalTaxAmount.toFixed(2)} - Discount: {currentCurrencySymbol}{totalDiscountAmount.toFixed(2)})
                    </p>
                    {selectedCustomer && (
                        <div className="mt-4 mb-2 p-3 bg-blue-50 rounded border border-blue-200">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-sm font-medium">Available Deposit</div>
                                    <div className="text-lg font-bold">
                                        {currentCurrencySymbol}{customerDeposit.toFixed(2)}
                                    </div>
                                </div>

                                {loadingDeposit ? (
                                    <Loader2 className="animate-spin text-blue-600" size={20} />
                                ) : customerDeposit > 0 ? (
                                    <Button
                                        variant={useDeposit ? "success" : "outline"}
                                        size="sm"
                                        onClick={() => setUseDeposit(prev => !prev)}
                                        disabled={customerDeposit <= 0}
                                    >
                                        {useDeposit ? "✓ Applied" : "Apply Deposit"}
                                    </Button>
                                ) : (
                                    <span className="text-sm text-gray-500">No deposit</span>
                                )}
                            </div>

                            {useDeposit && customerDeposit > 0 && (
                                <div className="mt-2 text-sm text-green-700">
                                    New balance due: {currentCurrencySymbol}
                                    {Math.max(0, grandTotal - customerDeposit).toFixed(2)}
                                </div>
                            )}
                        </div>
                    )}
                    {requiresManagerApproval && (
                        <div className="bg-orange-100 border-l-4 border-orange-500 text-orange-700 p-3 mb-4 rounded" role="alert">
                            <p className="font-bold flex items-center gap-2">
                                <AlertTriangle size={20} /> Manager Approval Required!
                            </p>
                            <p className="text-sm">This order includes discounts that require manager approval. Finalization is blocked until approved.</p>
                        </div>
                    )}
                    {selectedCustomer && (
                        <div className="mb-4 p-3 bg-gray-50 rounded-md border border-gray-200">
                            <p className="text-sm font-medium text-gray-700 mb-1">Customer:</p>
                            <p className="font-semibold text-text-primary">
                                {selectedCustomer.user.first_name} {selectedCustomer.user.last_name}
                            </p>
                            {/* <p className="text-xs text-gray-600">
                                ({selectedCustomer.user.credential.logon_id})
                            </p> */}
                            {loadingLOC ? (
                                <div className="flex items-center text-sm text-gray-600 mt-2">
                                    <Loader2 className="animate-spin h-4 w-4 mr-1" /> Loading credit info...
                                </div>
                            ) : errorLOC ? (
                                <p className="text-sm text-red-600 mt-2">Error loading credit: {errorLOC}</p>
                            ) : customerLOC ? (
                                <div className="text-sm text-gray-700 mt-2">
                                    <p>Credit Limit: {currentCurrencySymbol}{Number(customerLOC.credit_limit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                    <p>Outstanding: {currentCurrencySymbol}{Number(customerLOC.current_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                    <p className="font-semibold">Available Credit: {currentCurrencySymbol}{Number(availableCredit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                    {!customerLOC.is_active && (
                                        <p className="text-red-500 font-semibold mt-1">Credit Line: Inactive</p>
                                    )}
                                </div>
                                // <div className="text-sm text-gray-700 mt-2">
                                //     <p>Credit Limit: {currentCurrencySymbol}{customerLOC.credit_limit.toFixed(2)}</p>
                                //     <p>Outstanding: {currentCurrencySymbol}{customerLOC.current_balance.toFixed(2)}</p>
                                //     <p className="font-semibold">Available Credit: {currentCurrencySymbol}{availableCredit.toFixed(2)}</p>
                                //     {!customerLOC.is_active && (
                                //         <p className="text-red-500 font-semibold mt-1">Credit Line: Inactive</p>
                                //     )}
                                // </div>
                            ) : (
                                <p className="text-sm text-gray-500 mt-2">No credit line found for this customer at this branch.</p>
                            )}
                        </div>
                    )}

                    {/* POS Terminal & Outstanding Summary Banner */}
                    {posPaidAmount > 0 && (
                        <div className="p-3 mb-4 bg-blue-50 border border-blue-200 rounded-lg flex flex-col gap-1">
                            <div className="flex justify-between items-center text-sm font-semibold text-blue-900">
                                <span>Terminal Amount Paid:</span>
                                <span>{currencyCodeToSymbolMap[selectedStore?.setccurr || 'NGN']}{posPaidAmount.toFixed(2)}</span>
                            </div>
                            <div className="flex justify-between items-center text-sm font-bold text-indigo-900">
                                <span>Remaining Amount Needed:</span>
                                <span className="text-red-600">{currencyCodeToSymbolMap[selectedStore?.setccurr || 'NGN']}{remainingBalance.toFixed(2)}</span>
                            </div>
                            {remainingBalance > 0 && (
                                <p className="text-xs text-blue-700 mt-1">
                                    Select another payment method below (e.g. Split, Card, Transfer, Credit) to settle the remaining balance.
                                </p>
                            )}
                        </div>
                    )}

                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                        {depositCoversAll ? (
                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                                <p className="font-medium text-green-800">Fully covered by deposit.</p>
                                <p className="text-sm text-green-700 mt-1">No additional payment required.</p>
                            </div>
                        ) : (
                            <div className="flex gap-4 flex-wrap">
                                <Button
                                    type="button"
                                    variant={selectedPaymentMethod === 'cash' ? 'primary' : 'secondary'}
                                    onClick={() => setSelectedPaymentMethod('cash')}
                                    className="flex-1 flex items-center justify-center gap-2 min-w-[120px]"
                                >
                                    <Banknote size={18} /> Cash
                                </Button>
                                <Button
                                    type="button"
                                    variant={selectedPaymentMethod === 'card' ? 'primary' : 'secondary'}
                                    onClick={() => setSelectedPaymentMethod('card')}
                                    className="flex-1 flex items-center justify-center gap-2 min-w-[120px]"
                                >
                                    <CreditCard size={18} /> Card
                                </Button>
                                <Button
                                    type="button"
                                    variant={selectedPaymentMethod === 'transfer' ? 'primary' : 'secondary'}
                                    onClick={() => {
                                        setSelectedPaymentMethod('transfer')
                                        console.log(preferredTerminalBankAccountId)
                                        if (preferredTerminalBankAccountId) {
                                            setSelectedTransferBankAccount(String(preferredTerminalBankAccountId));
                                        }
                                    }}
                                    className="flex-1 flex items-center justify-center gap-2 min-w-[120px]"
                                >
                                    <Banknote size={18} /> Transfer
                                </Button>
                                {/* Push to Terminal Button / Status */}
                                {/* <div className="mb-4"> */}
                                <Button
                                    type="button"
                                    variant={selectedPaymentMethod === 'pos' ? 'primary' : 'secondary'}
                                    onClick={handlePushToPOS}
                                    disabled={isPushingToPOS || remainingBalance === 0}
                                    className="flex-1 flex items-center justify-center gap-2 min-w-[120px] !bg-emerald-400 !hover:bg-emerald-600 text-white"
                                >
                                    {isPushingToPOS ? (
                                        <Loader2 className="animate-spin" size={16} />
                                        // <>
                                        //     <Loader2 className="animate-spin" size={16} />
                                        //     <span>Waiting for Terminal Payment ({currencyCodeToSymbolMap[selectedStore?.setccurr || 'NGN']}{remainingBalance.toFixed(2)})...</span>
                                        // </>
                                    ) : (
                                        <CreditCard size={16} />
                                        // <>
                                        //     <CreditCard size={16} />
                                        //     <span>Push {posPaidAmount > 0 ? "Remaining Balance" : "Payment"} to Terminal</span>
                                        // </>
                                    )}
                                    <span>Terminal</span>
                                </Button>
                                {/* </div> */}
                                {/* <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={handlePushToPOS}
                                    className="flex-1 flex items-center justify-center gap-2 min-w-[120px]"
                                    disabled={isPushingToPOS || cart.length === 0}
                                >
                                    {isPushingToPOS ? <Loader2 className="animate-spin" size={18} /> : <CreditCard size={18} />}
                                    {posPaymentStatus === 'OPEN' ? 'Busy...' : 'Terminal'}
                                </Button> */}
                                <Button
                                    type="button"
                                    variant={selectedPaymentMethod === 'credit' ? 'primary' : 'secondary'}
                                    onClick={() => setSelectedPaymentMethod('credit')}
                                    className="flex-1 flex items-center justify-center gap-2 min-w-[120px]"
                                    disabled={
                                        !selectedCustomer ||
                                        loadingLOC ||
                                        !customerLOC ||
                                        !customerLOC.is_active ||
                                        // Math.max(0, grandTotal - (useDeposit ? customerDeposit : 0)) > availableCredit || depositCoversAll
                                        remainingDue > availableCredit ||
                                        depositCoversAll
                                    }
                                >
                                    <CreditCard size={18} /> Credit
                                </Button>
                                <Button
                                    type="button"
                                    variant={selectedPaymentMethod === 'split' ? 'primary' : 'secondary'}
                                    onClick={() => {
                                        setSelectedPaymentMethod('split')
                                        if (preferredTerminalBankAccountId) {
                                            setBankSplits(prev => {
                                                if (!prev.length) return prev;
                                                const next = [...prev];
                                                next[0] = {
                                                    ...next[0],
                                                    bankAccountId: String(preferredTerminalBankAccountId),
                                                }
                                                return next;
                                            });
                                        }
                                    }}
                                    className="flex-1 flex items-center justify-center gap-2 min-w-[120px]"
                                    disabled={cart.length === 0 || depositCoversAll}
                                // style={{ display: 'none'}}// REMOVE THIS LINE TO RE-ENABLE
                                >
                                    <SplitSquareVertical size={18} /> Split
                                </Button>
                                {terminalSurcharge > 0 && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        Terminal surcharge: {currentCurrencySymbol}{terminalSurcharge.toFixed(2)}
                                        (will be added to the amount sent to the device)
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                    {!depositCoversAll && selectedPaymentMethod && selectedPaymentMethod !== 'split' && selectedPaymentMethod !== 'credit' && (
                        <div className="mb-6">
                            <Input
                                type="number"
                                label="Amount Paid"
                                value={paymentAmount}
                                onChange={handlePaymentAmountChange}
                                placeholder="Enter amount paid"
                                className="w-full mb-2"
                                min="0"
                                step="0.01"
                                icon={<DollarSign size={18} />}
                            />
                            {(selectedPaymentMethod === 'card' || selectedPaymentMethod === 'transfer') && (
                                <SearchableSelectBox
                                    label={`Select Bank Account for ${selectedPaymentMethod === 'card' ? 'Card' : 'Transfer'} Payment`}
                                    id={`${selectedPaymentMethod}-bank-account`}
                                    name={`${selectedPaymentMethod}-bank-account`}
                                    value={selectedPaymentMethod === 'card' ? selectedCardBankAccount : selectedTransferBankAccount}
                                    onChange={(e) => {
                                        if (selectedPaymentMethod === 'card') {
                                            setSelectedCardBankAccount(e.target.value);
                                        } else {
                                            setSelectedTransferBankAccount(e.target.value);
                                        }
                                    }}
                                    options={bankAccountOptions}
                                    placeholder="Select a bank account"
                                    loading={loadingBankAccounts}
                                    error={errorBankAccounts}
                                    className="w-full mb-2"
                                />
                            )}
                            {/* {changeDue > 0 && (
                                <p className="text-sm text-green-600 mt-2">
                                    Change Due: {currentCurrencySymbol}{changeDue.toFixed(2)}
                                </p>
                            )}
                            {changeDue < 0 && (
                                <p className="text-sm text-red-600 mt-2">
                                    Insufficient Amount: {currentCurrencySymbol}{Math.abs(changeDue).toFixed(2)} still due
                                </p>
                            )} */}
                            {changeDue > 0.005 && (
                                <p className="text-sm text-green-600 mt-2">
                                    Change Due: {currentCurrencySymbol}{changeDue.toFixed(2)}
                                </p>
                            )}
                            {changeDue < -0.005 && (
                                <p className="text-sm text-red-600 mt-2">
                                    Insufficient Amount: {currentCurrencySymbol}{Math.abs(changeDue).toFixed(2)} still due
                                </p>
                            )}
                        </div>
                    )}
                    {depositCoversAll && (
                        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                            <p className="text-lg font-medium text-green-800">No additional payment needed</p>
                            <p className="text-sm text-green-700 mt-1">Full amount covered by customer deposit</p>
                        </div>
                    )}
                    {selectedPaymentMethod === 'credit' && (
                        <div className="mb-6">
                            <Input
                                type="date"
                                label="Payment Due Date"
                                value={paymentDueDate}
                                onChange={(e) => setPaymentDueDate(e.target.value)}
                                className="w-full mb-2"
                                min={new Date().toISOString().split('T')[0]}
                                placeholder="Select due date (default: 6 days from today)"
                            />
                            <p className="text-sm text-gray-600">
                                {/* Total Due: {currentCurrencySymbol}{grandTotal.toFixed(2)} (Covered by Credit) */}
                                {/* Total Due: {currentCurrencySymbol}
                                {useDeposit
                                    ? Math.max(0, grandTotal - customerDeposit).toFixed(2)
                                    : grandTotal.toFixed(2)
                                } (Covered by Credit) */}
                                Total Due: {currentCurrencySymbol}{remainingDue.toFixed(2)} (Covered by Credit)
                            </p>
                            {customerLOC && (
                                <p className="text-xs text-gray-500 mt-1">
                                    Available credit: {currentCurrencySymbol}{availableCredit.toFixed(2)}
                                </p>
                            )}
                        </div>
                    )}
                    {selectedPaymentMethod === 'split' && (
                        <div className="mb-6">
                            <p className="text-sm font-medium text-gray-700 mb-3">Split Payment Amounts</p>

                            {/* Cash */}
                            <Input
                                type="number"
                                label="Cash Amount"
                                value={splitCashAmount}
                                onChange={(e) => setSplitCashAmount(e.target.value)}
                                placeholder="Enter cash amount"
                                className="w-full mb-4"
                                min="0"
                                step="0.01"
                                icon={<Banknote size={18} />}
                            />

                            <div className="mb-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Terminal / POS Amount
                                </label>
                                <div className="flex items-center gap-3">
                                    <Input
                                        type="number"
                                        value={splitPosAmount}
                                        onChange={(e) => setSplitPosAmount(e.target.value)}
                                        placeholder="Amount to collect on terminal"
                                        className="flex-1"
                                        min="0"
                                        step="0.01"
                                        icon={<CreditCard size={18} />}
                                        disabled={isPushingToPOS || posPaidAmount > 0}   // lock after success
                                    />
                                    <Button
                                        type="button"
                                        variant="secondary"
                                        size="sm"
                                        disabled={
                                            isPushingToPOS ||
                                            !splitPosAmount ||
                                            parseFloat(splitPosAmount) <= 0 ||
                                            posPaidAmount > 0          // already collected
                                        }
                                        onClick={() => handlePushToPOS(parseFloat(splitPosAmount))}
                                        className="mb-4 !bg-emerald-400 !hover:bg-emerald-600 text-white flex items-center gap-2 whitespace-nowrap"
                                    >
                                        {isPushingToPOS ? (
                                            <Loader2 className="animate-spin" size={16} />
                                        ) : (
                                            <CreditCard size={16} />
                                        )}
                                        {posPaidAmount > 0 ? 'Paid on Terminal' : 'Push to Terminal'}
                                    </Button>
                                </div>
                                {posPaidAmount > 0 && (
                                    <p className="text-sm text-green-700 mt-2">
                                        ✓ Toward sale: {currentCurrencySymbol}{posPaidAmount.toFixed(2)}
                                        {surchargeCollected > 0 && (
                                            <> · Surcharge collected: {currentCurrencySymbol}{surchargeCollected.toFixed(2)}</>
                                        )}
                                        {posTransactionRef && ` (Ref: ${posTransactionRef})`}
                                    </p>
                                )}
                                {/* {posPaidAmount > 0 && (
                                    <p className="text-sm text-green-700 mt-2">
                                        ✓ Terminal settled: {currentCurrencySymbol}{posPaidAmount.toFixed(2)}
                                        {posTransactionRef && ` (Ref: ${posTransactionRef})`}
                                    </p>
                                )} */}
                            </div>

                            {/* Dynamic Bank Splits (Card or Transfer) */}
                            <div className="space-y-4 mb-4">
                                {bankSplits.map((split, index) => (
                                    <div key={split.id} className="flex items-center gap-3">
                                        <div className="flex-1">
                                            <Input
                                                type="number"
                                                label={index === 0 ? "Bank Payment Amount" : undefined}
                                                value={split.amount}
                                                onChange={(e) => {
                                                    const newSplits = [...bankSplits];
                                                    newSplits[index].amount = e.target.value;
                                                    setBankSplits(newSplits);
                                                }}
                                                placeholder="Enter amount"
                                                min="0"
                                                step="0.01"
                                                icon={<DollarSign size={18} />}
                                            />
                                        </div>

                                        <div className="flex-1">
                                            <SearchableSelectBox
                                                label={index === 0 ? "Bank Account" : undefined}
                                                value={split.bankAccountId}
                                                onChange={(e) => {
                                                    const newSplits = [...bankSplits];
                                                    newSplits[index].bankAccountId = e.target.value;
                                                    setBankSplits(newSplits);
                                                }}
                                                options={bankAccountOptions}
                                                placeholder="Select bank account"
                                                loading={loadingBankAccounts}
                                                error={errorBankAccounts}
                                            />
                                        </div>

                                        <div className="flex flex-col gap-2 pb-1">
                                            <label className={`text-xs font-medium ${index === 0 ? 'visible' : 'invisible'}`}>
                                                Payment Type
                                            </label>
                                            <div className="flex gap-4">
                                                <label className="flex items-center gap-1 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name={`paymentType-${split.id}`}
                                                        checked={split.paymentType === 'card'}
                                                        onChange={() => {
                                                            const newSplits = [...bankSplits];
                                                            newSplits[index].paymentType = 'card';
                                                            setBankSplits(newSplits);
                                                        }}
                                                        className="cursor-pointer"
                                                    />
                                                    <CreditCard size={16} />
                                                    <span className="text-xs">Card</span>
                                                </label>
                                                <label className="flex items-center gap-1 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name={`paymentType-${split.id}`}
                                                        checked={split.paymentType === 'transfer'}
                                                        onChange={() => {
                                                            const newSplits = [...bankSplits];
                                                            newSplits[index].paymentType = 'transfer';
                                                            setBankSplits(newSplits);
                                                        }}
                                                        className="cursor-pointer"
                                                    />
                                                    <Banknote size={16} />
                                                    <span className="text-xs">Transfer</span>
                                                </label>
                                            </div>
                                        </div>

                                        {bankSplits.length > 1 && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setBankSplits(bankSplits.filter((_, i) => i !== index))}
                                                className="text-red-600 hover:bg-red-50 mb-1"
                                            >
                                                <X size={18} />
                                            </Button>
                                        )}
                                    </div>
                                ))}

                                <Button
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => {
                                        setBankSplits([...bankSplits, {
                                            id: nextBankSplitId,
                                            amount: '',
                                            bankAccountId: '',
                                            paymentType: 'card'
                                        }]);
                                        setNextBankSplitId(prev => prev + 1);
                                    }}
                                    className="flex items-center gap-2"
                                >
                                    <Plus size={16} /> Add Another Bank Payment
                                </Button>
                            </div>

                            {/* Credit */}
                            <Input
                                type="number"
                                label="Credit Amount"
                                value={splitCreditAmount}
                                onChange={(e) => setSplitCreditAmount(e.target.value)}
                                placeholder="Enter credit amount"
                                // placeholder={`${splitRemaining > availableCredit}`}
                                className="w-full mb-4"
                                min="0"
                                step="0.01"
                                icon={<CreditCard size={18} />}
                                disabled={!customerLOC || !customerLOC.is_active || splitRemaining <= 0 || splitRemaining > availableCredit}
                            />

                            {/* Totals */}
                            <p className="text-sm font-semibold text-gray-700">
                                Total Split: {currentCurrencySymbol}{splitTotal.toFixed(2)}
                            </p>
                            {splitRemaining !== 0 && (
                                <p className={`text-sm mt-1 ${splitRemaining > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                    {splitRemaining > 0 ? 'Remaining' : 'Overpaid'}: {currentCurrencySymbol}{Math.abs(splitRemaining).toFixed(2)}
                                </p>
                            )}
                            {splitRemaining > 0 && (
                                <p className="text-sm text-red-600 mt-1">
                                    Remaining: {currentCurrencySymbol}{splitRemaining.toFixed(2)}
                                </p>
                            )}
                            {splitRemaining < 0 && (
                                <p className="text-sm text-red-600 mt-1">
                                    Overpaid: {currentCurrencySymbol}{Math.abs(splitRemaining).toFixed(2)}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Optional transaction date override */}
                    <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                        <CheckboxInput
                            id="override-transaction-date"
                            name="override-transaction-date"
                            label="Override transaction date"
                            checked={overrideTransactionDate}
                            onChange={(e) => {
                                const checked = e.target.checked;
                                setOverrideTransactionDate(checked);
                                if (!checked) {
                                    setTransactionDate('');
                                } else if (!transactionDate) {
                                    // default to now in local datetime-local format
                                    const now = new Date();
                                    const pad = (n) => String(n).padStart(2, '0');
                                    const local = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
                                    setTransactionDate(local);
                                }
                            }}
                        />
                        {overrideTransactionDate && (
                            <DateTimeInput
                                id="transaction-date"
                                name="transaction-date"
                                label="Transaction date & time"
                                value={transactionDate}
                                onChange={(e) => setTransactionDate(e.target.value)}
                                required
                                className="mt-1"
                            />
                        )}
                    </div>
                    <div className="flex gap-4">
                        <Button
                            variant="primary"
                            onClick={handleFinalizePayment}
                            className="flex-1 flex items-center justify-center gap-2"
                            disabled={
                                !canFinalize
                                // isFinalizingPayment ||
                                // !selectedPaymentMethod ||
                                // (selectedPaymentMethod === 'split' && Math.abs(splitTotal - grandTotal) > 0.01) ||
                                // (selectedPaymentMethod === 'credit' && !paymentDueDate) ||
                                // ((selectedPaymentMethod === 'card' && !selectedCardBankAccount) || (selectedPaymentMethod === 'transfer' && !selectedTransferBankAccount)) ||
                                // (selectedPaymentMethod !== 'split' && selectedPaymentMethod !== 'credit' && parseFloat(paymentAmount) < grandTotal)
                            }
                        >
                            {isFinalizingPayment ? (
                                <>
                                    <Loader2 size={24} className="animate-spin" />
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <CheckCircle size={24} /> 
                                    {/* {depositCoversAll ? "Complete Sale - Deposit Only" : "Finalize Payment"} */}
                                    {depositCoversAll
                                        ? 'Complete Sale - Deposit Only'
                                        : (requiresCreditApproval && (
                                            selectedPaymentMethod === 'credit' || 
                                            (selectedPaymentMethod === 'split' && parseFloat(splitCreditAmount || '0') > 0)
                                        ))
                                        ? 'Submit for Approval'
                                        : 'Finalize Payment'
                                    }
                                </>
                            )}
                        </Button>
                        <Button
                            variant="secondary"
                            // onClick={() => setShowCheckoutModal(false)}
                            onClick={closeCheckoutModal}
                            className="flex-1 flex items-center justify-center gap-2"
                        >
                            <X size={24} /> Cancel
                        </Button>
                    </div>
                </div>
            </Modal>
            <Modal
                isOpen={showPendingOrdersModal}
                onClose={() => setShowPendingOrdersModal(false)}
                title="Pending Orders"
                size='wide'
            >
                <div className="p-6">
                    {/* ... (loading and error states) */}
                    <table className="min-w-full text-sm text-text-primary">
                        <thead>
                            <tr>
                                <th className="px-2 py-2 text-left font-semibold text-gray-700">Order ID</th>
                                <th className="px-2 py-2 text-left font-semibold text-gray-700">Items</th>
                                <th className="px-2 py-2 text-left font-semibold text-gray-700">Total Amount</th>
                                <th className="px-2 py-2 text-left font-semibold text-gray-700">Currency</th>
                                <th className="px-2 py-2 text-left font-semibold text-gray-700">Status</th>
                                <th className="px-2 py-2 text-left font-semibold text-gray-700">Placed On</th>
                                <th className="px-2 py-2 text-right font-semibold text-gray-700">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pendingOrders.map(order => (
                                <tr key={order.idempotency_key} className="border-b border-gray-100 last:border-b-0">
                                    <td className="px-2 py-2">{order.idempotency_key}</td>
                                    <td className="px-2 py-2">
                                        {order.items.map(item => item.name).join(', ')}
                                    </td>
                                    <td className="px-2 py-2">{currencyCodeToSymbolMap[order.currency] || order.currency}{order.totalamount?.toFixed(2) || '0.00'}</td>
                                    <td className="px-2 py-2">{order.currency}</td>
                                    <td className="px-2 py-2">{order.status || 'Pending'}</td>
                                    <td className="px-2 py-2">{order.placed_on ? new Date(order.placed_on).toLocaleString() : 'N/A'}</td>
                                    <td className="px-2 py-2 text-right flex gap-2">
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => handleCheckoutPendingOrder(order)}
                                            className="px-3 py-1.5 flex items-center gap-2"
                                            disabled={loadingPendingOrders}
                                        >
                                            <CheckCircle size={14} /> Checkout
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => handleCancelOrder(order.orders_id, order.idempotency_key)}
                                            className="px-3 py-1.5 flex items-center gap-2"
                                            disabled={loadingPendingOrders}
                                        >
                                            <XCircle size={14} /> Cancel
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Modal>
            <ReceiptInvoiceModal
                isOpen={showReceiptInvoiceModal}
                onClose={() => setShowReceiptInvoiceModal(false)}
                receiptData={receiptData}
            />
            {/* <TrialModal isOpen={showTrialModal} onRequestClose={() => setShowTrialModal(false)} durationMs={6000} /> */}
        </div>
    );
};

export default PointOfSale;